import React, { useState, useEffect, useCallback, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { 
  fetchCart,
  removeFromCart,
  updateItemQuantity,
  clearCart
} from "../redux/thunks/cartThunks";
import ShippingAddress from "../Page/SubHeaderPage/Grocery/ShippingAddress";
import { RiDeleteBin6Line } from "react-icons/ri";
import Header from "../Page/Header/Header";
import Cookies from "js-cookie";
import { useNavigate } from "react-router-dom";
import Footer from "../Components/Footer/Footer";

const CartPage = () => {
  const { items: cart, totalItems, totalPrice, loading, error } = useSelector(state => state.cart);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [couponCode, setCouponCode] = useState("");
  const [showCouponInput, setShowCouponInput] = useState(false);
  const [appliedDiscount, setAppliedDiscount] = useState(0);
  const [showShippingAddress, setShowShippingAddress] = useState(false);
  const debounceTimeout = useRef(null);

  const handleQuantityChange = async (productId, variantId, newQuantity) => {
    try {
      await dispatch(updateItemQuantity({ productId, variantId, quantity: newQuantity }));
    } catch (error) {
      console.error("Error updating quantity:", error);
      alert("Failed to update quantity. Please try again.");
    }
  };

  const handleRemove = async (item) => {
    try {
      const productId = item.productId;
      const variantId = item.variantId
      console.log("prod",productId)
      console.log("item",item)
      await dispatch(removeFromCart({ productId, variantId }));
    } catch (error) {
      console.error("Error removing item:", error);
      alert("Failed to remove item from cart.");
    }
  };

  // Clean up timeout on component unmount
  useEffect(() => {
    return () => {
      if (debounceTimeout.current) {
        clearTimeout(debounceTimeout.current);
      }
    };
  }, []);

  // Fetch cart when component mounts
  useEffect(() => {
    const fetchCartData = async () => {
      try {
        await dispatch(fetchCart());
      } catch (error) {
        console.error("Failed to fetch cart:", error);
      }
    };

    fetchCartData();
  }, [dispatch]);

  const applyDiscount = () => {
    if (couponCode.toUpperCase() === "DISCOUNT10") {
      const discount = totalPrice * 0.1;
      setAppliedDiscount(discount);
      alert("Discount applied successfully!");
    } else {
      alert("Invalid discount code");
    }
  };

  const handleContinue = () => {
    setShowShippingAddress(true);
  };

  const handleShippingSubmit = (address) => {
    console.log("Shipping Address:", address);
    setShowShippingAddress(false);
  };

  const renderQuantityControls = (item) => {
    return (
      <div className="flex items-center justify-center space-x-2">
        <button
          className={`w-8 h-8 flex items-center justify-center rounded-md transition-colors ${
            item.quantity <= 1
              ? "bg-gray-100 text-gray-400 cursor-not-allowed"
              : "bg-gray-200 hover:bg-gray-300"
          }`}
          onClick={(e) => {
            e.stopPropagation();
            handleQuantityChange(item.productId, item.variantId, item.quantity - 1);
          }}
          disabled={item.quantity <= 1}
        >
          <span className="text-lg">−</span>
        </button>
        <span className="w-8 text-center">
          {item.quantity}
        </span>
        <button
          className="w-8 h-8 flex items-center justify-center bg-gray-200 rounded-md hover:bg-gray-300 transition-colors"
          onClick={(e) => {
            e.stopPropagation();
            handleQuantityChange(item.productId, item.variantId, item.quantity + 1);
          }}
        >
          <span className="text-lg">+</span>
        </button>
      </div>
    );
  };

  if (error) {
    return (
      <>
        <Header />
        <div className="w-full md:w-[96vw] mx-auto p-4 md:p-6 bg-gray-100 mt-8">
          <div className="bg-white p-4 rounded-lg shadow text-center">
            <p className="text-red-500 mb-4">Error loading cart: {error}</p>
            <button 
              onClick={() => dispatch(fetchCart())}
              className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
            >
              Retry
            </button>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Header/>
      <div className="w-full md:w-[96vw] mx-auto p-4 md:p-6 bg-gray-100 mt-8">
        <h1 className="text-xl md:text-2xl font-bold mb-4 md:mb-6 flex items-center">
          <span className="text-xl text-blue-500 mr-2">🛒</span> Cart
          {totalItems > 0 && (
            <span className="ml-2 inline-flex items-center justify-center w-6 h-6 text-xs font-semibold text-white bg-red-600 rounded-full">
              {totalItems}
            </span>
          )}
        </h1>
        
        <div className="flex flex-col lg:flex-row gap-4 md:gap-8">
          {/* Cart Items Table */}
          <div className="lg:w-2/3">
            <div className="bg-white rounded-lg shadow overflow-hidden">
              {cart.length === 0 ? (
                <div className="text-center py-12">
                  <p className="text-gray-500 text-lg">Your cart is empty</p>
                </div>
              ) : (
                <>
                  {/* Desktop Table */}
                  <div className="hidden md:block">
                    <table className="w-full">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Product
                          </th>
                          <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Quantity
                          </th>
                          <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Subtotal
                          </th>
                          <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {cart.map((item) => (
                          <tr key={`${item.productId._id}-${item.variantId || 'no-variant'}`}>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center">
                                <div className="flex-shrink-0 h-20 w-20">
                                  <img
                                    className="h-20 w-20 object-cover rounded cursor-pointer"
                                    src={item.image || '../../../assets/brand-default.jpg'}
                                    alt={item.name}
                                    onClick={() => navigate(`/product/${item.productId}`)}
                                  />
                                </div>
                                <div className="ml-4">
                                  <div className="text-sm font-medium text-gray-900">
                                    {item.name}
                                  </div>
                                  {item.variantName && (
                                    <div className="text-sm text-gray-500">
                                      Variant: {item.variantName}
                                    </div>
                                  )}
                                  <div className="text-sm text-gray-500">
                                    {item.price.toFixed(2)} Rs
                                  </div>
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              {renderQuantityControls(item)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                              {(item.price * item.quantity).toFixed(2)} Rs
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                              <button
                                onClick={() => handleRemove(item)}
                                className="text-red-600 hover:text-red-900 transition-colors"
                              >
                                <span className="text-lg"><RiDeleteBin6Line/></span>
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Mobile List */}
                  <div className="md:hidden">
                    {cart.map((item) => (
                      <div key={`${item.productId._id}-${item.variantId || 'no-variant'}`} className="p-4 border-b border-gray-200">
                        <div className="flex justify-between items-start">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 h-16 w-16">
                              <img
                                className="h-16 w-16 object-cover rounded"
                                src={item.image || '../../../assets/brand-default.jpg'}
                                alt={item.name}
                              />
                            </div>
                            <div className="ml-3">
                              <div className="text-sm font-medium text-gray-900">
                                {item.name}
                              </div>
                              {item.variantName && (
                                <div className="text-sm text-gray-500">
                                  Variant: {item.variantName}
                                </div>
                              )}
                              <div className="text-sm text-gray-500">
                                {item.price.toFixed(2)} Rs
                              </div>
                            </div>
                          </div>
                          <button
                            onClick={() => handleRemove(item)}
                            className="text-red-600 hover:text-red-900 transition-colors"
                          >
                            <RiDeleteBin6Line size={18} />
                          </button>
                        </div>
                        
                        <div className="mt-3 flex justify-between items-center">
                          {renderQuantityControls(item)}
                          <div className="text-sm font-medium">
                            Subtotal: {(item.price * item.quantity).toFixed(2)} Rs
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Order Summary */}
          <div className="lg:w-1/3">
            <div className="bg-white rounded-lg shadow p-4 md:p-6">
              <h2 className="text-lg font-semibold mb-4">Order Summary</h2>
              <div className="space-y-2 mb-4">
                <div className="flex justify-between text-sm">
                  <span>Subtotal</span>
                  <span>{totalPrice.toFixed(2)} Rs</span>
                </div>
                {/* <div className="flex justify-between text-sm">
                  <span>Shipping Charges</span>
                  <span>{cart.length > 0 ? 2.5 : 0} Rs</span>
                </div> */}
                {/* {appliedDiscount > 0 && (
                  <div className="flex justify-between text-sm text-green-600">
                    <span>Discount</span>
                    <span>-{appliedDiscount.toFixed(2)} Rs</span>
                  </div>
                )} */}
                <div className="flex justify-between font-semibold text-lg pt-2 border-t">
                  <span>Total</span>
                  <span>{totalPrice} Rs</span>
                </div>
              </div>
              
              <button
                className={`w-full py-2 px-4 rounded-md transition duration-200 mb-4 ${
                  cart.length === 0
                    ? "bg-gray-400 text-gray-200 cursor-not-allowed"
                    : "bg-blue-500 text-white hover:bg-blue-600"
                }`}
                onClick={handleContinue}
                disabled={cart.length === 0}
              >
                Continue
              </button>
              
              {/* <div className="flex items-center mb-2">
                <input
                  type="checkbox"
                  id="useDiscount"
                  className="mr-2"
                  checked={showCouponInput}
                  onChange={() => setShowCouponInput(!showCouponInput)}
                  disabled={cart.length === 0}
                />
                <label 
                  htmlFor="useDiscount" 
                  className={`text-sm ${cart.length === 0 ? "text-gray-400" : "text-gray-600"}`}
                >
                  Use discount code
                </label>
              </div> */}
              
              {/* {showCouponInput && (
                <div className="flex space-x-2">
                  <input
                    type="text"
                    placeholder="Enter code"
                    value={couponCode}
                    onChange={(e) => setCouponCode(e.target.value)}
                    className="flex-grow px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <button
                    onClick={applyDiscount}
                    className="bg-blue-500 text-white px-4 py-2 rounded-md hover:bg-blue-600 transition duration-200"
                  >
                    Apply
                  </button>
                </div>
              )} */}
            </div>
          </div>
        </div>
        

        {showShippingAddress && (
          <ShippingAddress
            onClose={() => setShowShippingAddress(false)}
            onSubmit={handleShippingSubmit}
            orderSummary={{
              subtotal: totalPrice,
              shippingCharges: cart.length > 0 ? 2.5 : 0,
              taxes: cart.length > 0 ? totalPrice * 0.05 : 0,
              appliedDiscount,
              total: totalPrice + (cart.length > 0 ? 2.5 : 0) - appliedDiscount,
            }}
            clearCart={() => dispatch(clearCart())}
            fetchCart={() => dispatch(fetchCart())}
          />
        )}
      </div>
      <Footer />
    </>
  );
};

export default CartPage;

// import React, { useState, useEffect, useCallback, useRef } from "react";
// import { useDispatch, useSelector } from "react-redux";
// import { 
//   fetchCart,
//   removeFromCart,
//   updateItemQuantity,
//   clearCart
// } from "../redux/thunks/cartThunks";
// import ShippingAddress from "../Page/SubHeaderPage/Grocery/ShippingAddress";
// import { RiDeleteBin6Line } from "react-icons/ri";
// import Header from "../Page/Header/Header";
// import Cookies from "js-cookie";
// import { useNavigate } from "react-router-dom";

// const CartPage = () => {
//   const { items: cart, totalItems, totalPrice, loading, error } = useSelector(state => state.cart);
//   const dispatch = useDispatch();
//   const navigate = useNavigate();
//   const [couponCode, setCouponCode] = useState("");
//   const [showCouponInput, setShowCouponInput] = useState(false);
//   const [appliedDiscount, setAppliedDiscount] = useState(0);
//   const [showShippingAddress, setShowShippingAddress] = useState(false);
//   const debounceTimeout = useRef(null);

//   const handleQuantityChange = async (productId, variantId, newQuantity) => {
//     try {
//       await dispatch(updateItemQuantity({ productId, variantId, quantity: newQuantity }));
//     } catch (error) {
//       console.error("Error updating quantity:", error);
//       alert("Failed to update quantity. Please try again.");
//     }
//   };

//   const handleRemove = async (item) => {
//     try {
//       const productId = item.productId;
//       const variantId = item.variantId
//       console.log("prod",productId)
//       console.log("item",item)
//       await dispatch(removeFromCart({ productId, variantId }));
//     } catch (error) {
//       console.error("Error removing item:", error);
//       alert("Failed to remove item from cart.");
//     }
//   };

//   // Clean up timeout on component unmount
//   useEffect(() => {
//     return () => {
//       if (debounceTimeout.current) {
//         clearTimeout(debounceTimeout.current);
//       }
//     };
//   }, []);

//   // Fetch cart when component mounts
//   useEffect(() => {
//     const fetchCartData = async () => {
//       try {
//         await dispatch(fetchCart());
//       } catch (error) {
//         console.error("Failed to fetch cart:", error);
//       }
//     };

//     fetchCartData();
//   }, [dispatch]);

//   const applyDiscount = () => {
//     if (couponCode.toUpperCase() === "DISCOUNT10") {
//       const discount = totalPrice * 0.1;
//       setAppliedDiscount(discount);
//       alert("Discount applied successfully!");
//     } else {
//       alert("Invalid discount code");
//     }
//   };

//   const handleContinue = () => {
//     setShowShippingAddress(true);
//   };

//   const handleShippingSubmit = (address) => {
//     console.log("Shipping Address:", address);
//     setShowShippingAddress(false);
//   };

//   const renderQuantityControls = (item) => {
//     return (
//       <div className="flex items-center justify-center space-x-2">
//         <button
//           className={`w-8 h-8 flex items-center justify-center rounded-md transition-colors ${
//             item.quantity <= 1
//               ? "bg-gray-100 text-gray-400 cursor-not-allowed"
//               : "bg-gray-200 hover:bg-gray-300"
//           }`}
//           onClick={(e) => {
//             e.stopPropagation();
//             handleQuantityChange(item.productId, item.variantId, item.quantity - 1);
//           }}
//           disabled={item.quantity <= 1}
//         >
//           <span className="text-lg">−</span>
//         </button>
//         <span className="w-8 text-center">
//           {item.quantity}
//         </span>
//         <button
//           className="w-8 h-8 flex items-center justify-center bg-gray-200 rounded-md hover:bg-gray-300 transition-colors"
//           onClick={(e) => {
//             e.stopPropagation();
//             handleQuantityChange(item.productId, item.variantId, item.quantity + 1);
//           }}
//         >
//           <span className="text-lg">+</span>
//         </button>
//       </div>
//     );
//   };

//   if (error) {
//     return (
//       <>
//         <Header />
//         <div className="w-full md:w-[96vw] mx-auto p-4 md:p-6 bg-gray-100 mt-8">
//           <div className="bg-white p-4 rounded-lg shadow text-center">
//             <p className="text-red-500 mb-4">Error loading cart: {error}</p>
//             <button 
//               onClick={() => dispatch(fetchCart())}
//               className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
//             >
//               Retry
//             </button>
//           </div>
//         </div>
//       </>
//     );
//   }

//   return (
//     <>
//       <Header/>
//       <div className="w-full md:w-[96vw] mx-auto p-4 md:p-6 bg-gray-100 mt-8">
//         <h1 className="text-xl md:text-2xl font-bold mb-4 md:mb-6 flex items-center">
//           <span className="text-xl text-blue-500 mr-2">🛒</span> Cart
//           {totalItems > 0 && (
//             <span className="ml-2 inline-flex items-center justify-center w-6 h-6 text-xs font-semibold text-white bg-red-600 rounded-full">
//               {totalItems}
//             </span>
//           )}
//         </h1>
        
//         <div className="flex flex-col lg:flex-row gap-4 md:gap-8">
//           {/* Cart Items Table */}
//           <div className="lg:w-2/3">
//             <div className="bg-white rounded-lg shadow overflow-hidden">
//               {cart.length === 0 ? (
//                 <div className="text-center py-12">
//                   <p className="text-gray-500 text-lg">Your cart is empty</p>
//                 </div>
//               ) : (
//                 <>
//                   {/* Desktop Table */}
//                   <div className="hidden md:block">
//                     <table className="w-full">
//                       <thead className="bg-gray-50">
//                         <tr>
//                           <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
//                             Product
//                           </th>
//                           <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
//                             Quantity
//                           </th>
//                           <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
//                             Subtotal
//                           </th>
//                           <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
//                             Actions
//                           </th>
//                         </tr>
//                       </thead>
//                       <tbody className="divide-y divide-gray-200">
//                         {cart.map((item) => (
//                           <tr key={`${item.productId._id}-${item.variantId || 'no-variant'}`}>
//                             <td className="px-6 py-4 whitespace-nowrap">
//                               <div className="flex items-center">
//                                 <div className="flex-shrink-0 h-20 w-20">
//                                   <img
//                                     className="h-20 w-20 object-cover rounded cursor-pointer"
//                                     src={item.image || '../../../assets/brand-default.jpg'}
//                                     alt={item.name}
//                                     onClick={() => navigate(`/product/${item.productId}`)}
//                                   />
//                                 </div>
//                                 <div className="ml-4">
//                                   <div className="text-sm font-medium text-gray-900">
//                                     {item.name}
//                                   </div>
//                                   {item.variantName && (
//                                     <div className="text-sm text-gray-500">
//                                       Variant: {item.variantName}
//                                     </div>
//                                   )}
//                                   <div className="text-sm text-gray-500">
//                                     {item.price.toFixed(2)} Rs
//                                   </div>
//                                 </div>
//                               </div>
//                             </td>
//                             <td className="px-6 py-4 whitespace-nowrap">
//                               {renderQuantityControls(item)}
//                             </td>
//                             <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
//                               {(item.price * item.quantity).toFixed(2)} Rs
//                             </td>
//                             <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
//                               <button
//                                 onClick={() => handleRemove(item)}
//                                 className="text-red-600 hover:text-red-900 transition-colors"
//                               >
//                                 <span className="text-lg"><RiDeleteBin6Line/></span>
//                               </button>
//                             </td>
//                           </tr>
//                         ))}
//                       </tbody>
//                     </table>
//                   </div>

//                   {/* Mobile List */}
//                   <div className="md:hidden">
//                     {cart.map((item) => (
//                       <div key={`${item.productId._id}-${item.variantId || 'no-variant'}`} className="p-4 border-b border-gray-200">
//                         <div className="flex justify-between items-start">
//                           <div className="flex items-center">
//                             <div className="flex-shrink-0 h-16 w-16">
//                               <img
//                                 className="h-16 w-16 object-cover rounded"
//                                 src={item.image || '../../../assets/brand-default.jpg'}
//                                 alt={item.name}
//                               />
//                             </div>
//                             <div className="ml-3">
//                               <div className="text-sm font-medium text-gray-900">
//                                 {item.name}
//                               </div>
//                               {item.variantName && (
//                                 <div className="text-sm text-gray-500">
//                                   Variant: {item.variantName}
//                                 </div>
//                               )}
//                               <div className="text-sm text-gray-500">
//                                 {item.price.toFixed(2)} Rs
//                               </div>
//                             </div>
//                           </div>
//                           <button
//                             onClick={() => handleRemove(item)}
//                             className="text-red-600 hover:text-red-900 transition-colors"
//                           >
//                             <RiDeleteBin6Line size={18} />
//                           </button>
//                         </div>
                        
//                         <div className="mt-3 flex justify-between items-center">
//                           {renderQuantityControls(item)}
//                           <div className="text-sm font-medium">
//                             Subtotal: {(item.price * item.quantity).toFixed(2)} Rs
//                           </div>
//                         </div>
//                       </div>
//                     ))}
//                   </div>
//                 </>
//               )}
//             </div>
//           </div>

//           {/* Order Summary */}
//           <div className="lg:w-1/3">
//             <div className="bg-white rounded-lg shadow p-4 md:p-6">
//               <h2 className="text-lg font-semibold mb-4">Order Summary</h2>
//               <div className="space-y-2 mb-4">
//                 <div className="flex justify-between text-sm">
//                   <span>Subtotal</span>
//                   <span>{totalPrice.toFixed(2)} Rs</span>
//                 </div>
//                 <div className="flex justify-between text-sm">
//                   <span>Shipping Charges</span>
//                   <span>{cart.length > 0 ? 2.5 : 0} Rs</span>
//                 </div>
//                 {appliedDiscount > 0 && (
//                   <div className="flex justify-between text-sm text-green-600">
//                     <span>Discount</span>
//                     <span>-{appliedDiscount.toFixed(2)} Rs</span>
//                   </div>
//                 )}
//                 <div className="flex justify-between font-semibold text-lg pt-2 border-t">
//                   <span>Total</span>
//                   <span>{(totalPrice + (cart.length > 0 ? 2.5 : 0) - appliedDiscount).toFixed(2)} Rs</span>
//                 </div>
//               </div>
              
//               <button
//                 className={`w-full py-2 px-4 rounded-md transition duration-200 mb-4 ${
//                   cart.length === 0
//                     ? "bg-gray-400 text-gray-200 cursor-not-allowed"
//                     : "bg-blue-500 text-white hover:bg-blue-600"
//                 }`}
//                 onClick={handleContinue}
//                 disabled={cart.length === 0}
//               >
//                 Continue
//               </button>
              
//               <div className="flex items-center mb-2">
//                 <input
//                   type="checkbox"
//                   id="useDiscount"
//                   className="mr-2"
//                   checked={showCouponInput}
//                   onChange={() => setShowCouponInput(!showCouponInput)}
//                   disabled={cart.length === 0}
//                 />
//                 <label 
//                   htmlFor="useDiscount" 
//                   className={`text-sm ${cart.length === 0 ? "text-gray-400" : "text-gray-600"}`}
//                 >
//                   Use discount code
//                 </label>
//               </div>
              
//               {showCouponInput && (
//                 <div className="flex space-x-2">
//                   <input
//                     type="text"
//                     placeholder="Enter code"
//                     value={couponCode}
//                     onChange={(e) => setCouponCode(e.target.value)}
//                     className="flex-grow px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
//                   />
//                   <button
//                     onClick={applyDiscount}
//                     className="bg-blue-500 text-white px-4 py-2 rounded-md hover:bg-blue-600 transition duration-200"
//                   >
//                     Apply
//                   </button>
//                 </div>
//               )}
//             </div>
//           </div>
//         </div>

//         {showShippingAddress && (
//           <ShippingAddress
//             onClose={() => setShowShippingAddress(false)}
//             onSubmit={handleShippingSubmit}
//             orderSummary={{
//               subtotal: totalPrice,
//               shippingCharges: cart.length > 0 ? 2.5 : 0,
//               taxes: cart.length > 0 ? totalPrice * 0.05 : 0,
//               appliedDiscount,
//               total: totalPrice + (cart.length > 0 ? 2.5 : 0) - appliedDiscount,
//             }}
//             clearCart={() => dispatch(clearCart())}
//             fetchCart={() => dispatch(fetchCart())}
//           />
//         )}
//       </div>
//     </>
//   );
// };

// export default CartPage;

// import React, { useState, useEffect, useCallback, useRef } from "react";
// import { useDispatch, useSelector } from "react-redux";
// import { 
//   fetchCart as fetchCartThunk,
//   removeFromCart as removeFromCartThunk,
//   updateItemQuantity,
//   clearCart as clearCartThunk
// } from "../redux/thunks/cartThunks";
// import ShippingAddress from "../Page/SubHeaderPage/Grocery/ShippingAddress";
// import { RiDeleteBin6Line } from "react-icons/ri";
// import Header from "../Page/Header/Header";
// import Cookies from "js-cookie";
// import { useNavigate } from "react-router-dom";

// const CartPage = () => {
//   const cart = useSelector(state => state.cart.items);
//   const { loading, error } = useSelector(state => state.cart);
//   const dispatch = useDispatch();
//   const navigate = useNavigate()
//   const [couponCode, setCouponCode] = useState("");
//   const [showCouponInput, setShowCouponInput] = useState(false);
//   const [appliedDiscount, setAppliedDiscount] = useState(0);
//   const [showShippingAddress, setShowShippingAddress] = useState(false);
//   const debounceTimeout = useRef(null);

//   // Function to parse price correctly
//   const parsePrice = (price) => {
//     if (typeof price === "number") return price;
    
//     if (typeof price === "string") {
//       const cleanedPrice = price.replace("₹", "").trim();
//       const parsedPrice = parseFloat(cleanedPrice) || 0;
//       return isNaN(parsedPrice) ? 0 : Math.max(0, parsedPrice);
//     }
    
//     return 0;
//   };

//   const calculateSubtotal = () => {
//     return cart.reduce((total, item) => {
//       const price = parsePrice(item?.price);
//       return total + price * item.quantity;
//     }, 0);
//   };

//   const applyDiscount = () => {
//     if (couponCode.toUpperCase() === "DISCOUNT10") {
//       const discount = calculateSubtotal() * 0.1;
//       setAppliedDiscount(discount);
//       alert("Discount applied successfully!");
//     } else {
//       alert("Invalid discount code");
//     }
//   };

//   // const handleQuantityChange = async (productId, newQuantity) => {
//   //   try {
//   //     // Update local state immediately for responsive UI
//   //     dispatch(updateItemQuantity({ productId, quantity: newQuantity }));

//   //     // Clear any existing timeout
//   //     if (debounceTimeout.current) {
//   //       clearTimeout(debounceTimeout.current);
//   //     }

//   //     // Set a new timeout for API call
//   //     debounceTimeout.current = setTimeout(async () => {
//   //       try {
//   //         await dispatch(updateItemQuantity({ productId, quantity: newQuantity }));
//   //       } catch (error) {
//   //         console.error("Error updating quantity:", error);
//   //         // Revert the quantity change if the API call fails
//   //         dispatch(updateItemQuantity({ productId, quantity: newQuantity > 0 ? newQuantity - 1 : 0 }));
//   //         alert("Failed to update cart item.");
//   //       }
//   //     }, 800); // 800ms delay
//   //   } catch (error) {
//   //     console.error("Error updating quantity:", error);
//   //     alert("Failed to update quantity. Please try again.");
//   //   }
//   // };
//   const handleQuantityChange = async (productId, newQuantity) => {
//       try {
//         console.log("handle quantity",newQuantity)
//         await dispatch(updateItemQuantity({ productId, quantity: newQuantity }));
//       } catch (error) {
//         console.error("Error updating quantity:", error);
//         alert("Failed to update quantity. Please try again.");
//       }
//     };

//   const handleRemove = async (id) => {
//     try {
//       await dispatch(removeFromCartThunk(id));
//     } catch (error) {
//       console.error("Error removing item:", error);
//       alert("Failed to remove item from cart.");
//     }
//   };

//   // Clean up timeout on component unmount
//   useEffect(() => {
//     return () => {
//       if (debounceTimeout.current) {
//         clearTimeout(debounceTimeout.current);
//       }
//     };
//   }, []);

//   // Fetch cart when component mounts
//   useEffect(() => {
//     const fetchCart = async () => {
//       try {
//         await dispatch(fetchCartThunk());
//       } catch (error) {
//         console.error("Failed to fetch cart:", error);
//       }
//     };

//     fetchCart();
//   }, [dispatch]);

//   const subtotal = calculateSubtotal();
//   const shippingCharges = cart.length > 0 ? 2.5 : 0;
//   const taxes = cart.length > 0 ? subtotal * 0.05 : 0;
//   const total = cart.length > 0 ? subtotal + shippingCharges : 0;

//   const handleContinue = () => {
//     setShowShippingAddress(true);
//   };

//   const handleShippingSubmit = (address) => {
//     console.log("Shipping Address:", address);
//     setShowShippingAddress(false);
//   };

//   const cartItemCount = cart.reduce((total, item) => total + item.quantity, 0);

//   const renderQuantityControls = (item) => {
//     console.log("inside cart item",item)
//     return (
//       <div className="flex items-center justify-center space-x-2">
//         <button
//           className={`w-8 h-8 flex items-center justify-center rounded-md transition-colors ${
//             item.quantity <= 0
//               ? "bg-gray-100 text-gray-400 cursor-not-allowed"
//               : "bg-gray-200 hover:bg-gray-300"
//           }`}
//           onClick={(e) => {
//             e.stopPropagation();
//             handleQuantityChange(item?.productId, item?.quantity - 1);
//           }}
//           disabled={item.quantity <= 0}
//         >
//           <span className="text-lg">−</span>
//         </button>
//         <span className="w-8 text-center">
//           {item?.quantity}
//         </span>
//         <button
//           className="w-8 h-8 flex items-center justify-center bg-gray-200 rounded-md hover:bg-gray-300 transition-colors"
//           onClick={(e) => {
//             e.stopPropagation();
//             handleQuantityChange(item?.productId, item?.quantity + 1);
//           }}
//         >
//           <span className="text-lg">+</span>
//         </button>
//       </div>
//     );
//   };

//   // if (loading) {
//   //   return (
//   //     <>
//   //       <Header />
//   //       <div className="w-full md:w-[96vw] mx-auto p-4 md:p-6 bg-gray-100 mt-8">
//   //         <div className="flex justify-center items-center h-64">
//   //           <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
//   //         </div>
//   //       </div>
//   //     </>
//   //   );
//   // }

//   if (error) {
//     return (
//       <>
//         <Header />
//         <div className="w-full md:w-[96vw] mx-auto p-4 md:p-6 bg-gray-100 mt-8">
//           <div className="bg-white p-4 rounded-lg shadow text-center">
//             <p className="text-red-500 mb-4">Error loading cart: {error}</p>
//             <button 
//               onClick={() => dispatch(fetchCartThunk())}
//               className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
//             >
//               Retry
//             </button>
//           </div>
//         </div>
//       </>
//     );
//   }

//   return (
//     <>
//       <Header/>
//       <div className="w-full md:w-[96vw] mx-auto p-4 md:p-6 bg-gray-100 mt-8">
//         <h1 className="text-xl md:text-2xl font-bold mb-4 md:mb-6 flex items-center">
//           <span className="text-xl text-blue-500 mr-2">🛒</span> Cart
//           {cartItemCount > 0 && (
//             <span className="ml-2 inline-flex items-center justify-center w-6 h-6 text-xs font-semibold text-white bg-red-600 rounded-full">
//               {cartItemCount}
//             </span>
//           )}
//         </h1>
        
//         <div className="flex flex-col lg:flex-row gap-4 md:gap-8">
//           {/* Cart Items Table */}
//           <div className="lg:w-2/3">
//             <div className="bg-white rounded-lg shadow overflow-hidden">
//               {cart.length === 0 ? (
//                 <div className="text-center py-12">
//                   <p className="text-gray-500 text-lg">Your cart is empty</p>
//                 </div>
//               ) : (
//                 <>
//                   {/* Desktop Table */}
//                   <div className="hidden md:block">
//                     <table className="w-full">
//                       <thead className="bg-gray-50">
//                         <tr>
//                           <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
//                             Product
//                           </th>
//                           <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
//                             Quantity
//                           </th>
//                           <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
//                             Subtotal
//                           </th>
//                           <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
//                             Actions
//                           </th>
//                         </tr>
//                       </thead>
//                       <tbody className="divide-y divide-gray-200">
//                         {cart.map((item) => {
//                           const price = parsePrice(item?.price);
//                           return (
//                             <tr key={item?.productId}>
//                               <td className="px-6 py-4 whitespace-nowrap">
//                                 <div className="flex items-center">
//                                   <div className="flex-shrink-0 h-20 w-20">
//                                     <img
//                                       className="h-20 w-20 object-cover rounded cursor-pointer"
//                                       src={item.image || '../../../assets/brand-default.jpg'}
//                                       alt={item.title}
//                                       onClick={()=>navigate(`/product/${item.productId}`)}
//                                     />
//                                   </div>
//                                   <div className="ml-4">
//                                     <div className="text-sm font-medium text-gray-900">
//                                       {item?.name}
//                                     </div>
//                                     <div className="text-sm text-gray-500">
//                                       {item?.price?.toFixed(2)} Rs
//                                     </div>
//                                   </div>
//                                 </div>
//                               </td>
//                               <td className="px-6 py-4 whitespace-nowrap">
//                                 {renderQuantityControls(item)}
//                               </td>
//                               <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
//                                 {(price * item?.quantity)?.toFixed(2)} Rs
//                               </td>
//                               <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
//                                 <button
//                                   onClick={() => handleRemove(item?.productId)}
//                                   className="text-red-600 hover:text-red-900 transition-colors"
//                                 >
//                                   <span className="text-lg"><RiDeleteBin6Line/></span>
//                                 </button>
//                               </td>
//                             </tr>
//                           );
//                         })}
//                       </tbody>
//                     </table>
//                   </div>

//                   {/* Mobile List */}
//                   <div className="md:hidden">
//                     {cart.map((item) => {
//                       const price = parsePrice(item?.price);
//                       return (
//                         <div key={item.id} className="p-4 border-b border-gray-200">
//                           <div className="flex justify-between items-start">
//                             <div className="flex items-center">
//                               <div className="flex-shrink-0 h-16 w-16">
//                                 <img
//                                   className="h-16 w-16 object-cover rounded"
//                                   src={item.image}
//                                   alt={item.title}
//                                 />
//                               </div>
//                               <div className="ml-3">
//                                 <div className="text-sm font-medium text-gray-900">
//                                   {item?.name}
//                                 </div>
//                                 <div className="text-sm text-gray-500">
//                                   {price?.toFixed(2)} Rs
//                                 </div>
//                               </div>
//                             </div>
//                             <button
//                               onClick={() => handleRemove(item.id)}
//                               className="text-red-600 hover:text-red-900 transition-colors"
//                             >
//                               <RiDeleteBin6Line size={18} />
//                             </button>
//                           </div>
                          
//                           <div className="mt-3 flex justify-between items-center">
//                             {renderQuantityControls(item)}
//                             <div className="text-sm font-medium">
//                               Subtotal: {(price * item?.quantity)?.toFixed(2)} Rs
//                             </div>
//                           </div>
//                         </div>
//                       );
//                     })}
//                   </div>
//                 </>
//               )}
//             </div>
//           </div>

//           {/* Order Summary */}
//           <div className="lg:w-1/3">
//             <div className="bg-white rounded-lg shadow p-4 md:p-6">
//               <h2 className="text-lg font-semibold mb-4">Order Summary</h2>
//               <div className="space-y-2 mb-4">
//                 <div className="flex justify-between text-sm">
//                   <span>Subtotal</span>
//                   <span>{subtotal?.toFixed(2)} Rs</span>
//                 </div>
//                 <div className="flex justify-between text-sm">
//                   <span>Shipping Charges</span>
//                   <span>{shippingCharges?.toFixed(2)} Rs</span>
//                 </div>
//                 <div className="flex justify-between font-semibold text-lg pt-2 border-t">
//                   <span>Total</span>
//                   <span>{total?.toFixed(2)} Rs</span>
//                 </div>
//               </div>
              
//               <button
//                 className={`w-full py-2 px-4 rounded-md transition duration-200 mb-4 ${
//                   cart.length === 0
//                     ? "bg-gray-400 text-gray-200 cursor-not-allowed"
//                     : "bg-blue-500 text-white hover:bg-blue-600"
//                 }`}
//                 onClick={handleContinue}
//                 disabled={cart?.length === 0}
//               >
//                 Continue
//               </button>
              
//               <div className="flex items-center mb-2">
//                 <input
//                   type="checkbox"
//                   id="useDiscount"
//                   className="mr-2"
//                   checked={showCouponInput}
//                   onChange={() => setShowCouponInput(!showCouponInput)}
//                   disabled={cart.length === 0}
//                 />
//                 <label 
//                   htmlFor="useDiscount" 
//                   className={`text-sm ${cart.length === 0 ? "text-gray-400" : "text-gray-600"}`}
//                 >
//                   Use discount code
//                 </label>
//               </div>
              
//               {showCouponInput && (
//                 <div className="flex space-x-2">
//                   <input
//                     type="text"
//                     placeholder="Enter code"
//                     value={couponCode}
//                     onChange={(e) => setCouponCode(e.target.value)}
//                     className="flex-grow px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
//                   />
//                   <button
//                     onClick={applyDiscount}
//                     className="bg-blue-500 text-white px-4 py-2 rounded-md hover:bg-blue-600 transition duration-200"
//                   >
//                     Apply
//                   </button>
//                 </div>
//               )}
//             </div>
//           </div>
//         </div>

//         {showShippingAddress && (
//           <ShippingAddress
//             onClose={() => setShowShippingAddress(false)}
//             onSubmit={handleShippingSubmit}
//             orderSummary={{
//               subtotal,
//               shippingCharges,
//               taxes,
//               appliedDiscount,
//               total,
//             }}
//             clearCart={() => dispatch(clearCartThunk())}
//             fetchCart={() => dispatch(fetchCartThunk())}
//           />
//         )}
//       </div>
//     </>
//   );
// };

// export default CartPage;

// import React, { useState, useEffect, useCallback, useRef } from "react";
// import { useDispatch, useSelector } from "react-redux";
// import { 
//   fetchCart as fetchCartThunk,
//   removeFromCart as removeFromCartThunk,
//   updateItemQuantity,
//   clearCart as clearCartThunk
// } from "../redux/thunks/cartThunks";
// import ShippingAddress from "../Page/SubHeaderPage/Grocery/ShippingAddress";
// import { RiDeleteBin6Line } from "react-icons/ri";
// import Header from "../Page/Header/Header";
// import Cookies from "js-cookie";

// const CartPage = () => {
//   const cart = useSelector(state => state.cart.items);
//   const { loading, error } = useSelector(state => state.cart);
//   const dispatch = useDispatch();
  
//   const [couponCode, setCouponCode] = useState("");
//   const [showCouponInput, setShowCouponInput] = useState(false);
//   const [appliedDiscount, setAppliedDiscount] = useState(0);
//   const [showShippingAddress, setShowShippingAddress] = useState(false);
//   const debounceTimeout = useRef(null);

//   // Function to parse price correctly
//   const parsePrice = (price) => {
//     if (typeof price === "number") return price;
    
//     if (typeof price === "string") {
//       const cleanedPrice = price.replace("₹", "").trim();
//       const parsedPrice = parseFloat(cleanedPrice) || 0;
//       return isNaN(parsedPrice) ? 0 : Math.max(0, parsedPrice);
//     }
    
//     return 0;
//   };

//   const calculateSubtotal = () => {
//     return cart.reduce((total, item) => {
//       const price = parsePrice(item?.price);
//       return total + price * item.quantity;
//     }, 0);
//   };

//   const applyDiscount = () => {
//     if (couponCode.toUpperCase() === "DISCOUNT10") {
//       const discount = calculateSubtotal() * 0.1;
//       setAppliedDiscount(discount);
//       alert("Discount applied successfully!");
//     } else {
//       alert("Invalid discount code");
//     }
//   };

//   const handleQuantityChange = async (productId, newQuantity) => {
//     try {
//       // Update local state immediately for responsive UI
//       dispatch(updateItemQuantity({ productId, quantity: newQuantity }));

//       // Clear any existing timeout
//       if (debounceTimeout.current) {
//         clearTimeout(debounceTimeout.current);
//       }

//       // Set a new timeout for API call
//       debounceTimeout.current = setTimeout(async () => {
//         try {
//           await dispatch(updateItemQuantity({ productId, quantity: newQuantity }));
//         } catch (error) {
//           console.error("Error updating quantity:", error);
//           // Revert the quantity change if the API call fails
//           dispatch(updateItemQuantity({ productId, quantity: newQuantity > 0 ? newQuantity - 1 : 0 }));
//           alert("Failed to update cart item.");
//         }
//       }, 800); // 800ms delay
//     } catch (error) {
//       console.error("Error updating quantity:", error);
//       alert("Failed to update quantity. Please try again.");
//     }
//   };

//   const handleRemove = async (id) => {
//     try {
//       await dispatch(removeFromCartThunk(id));
//     } catch (error) {
//       console.error("Error removing item:", error);
//       alert("Failed to remove item from cart.");
//     }
//   };

//   // Clean up timeout on component unmount
//   useEffect(() => {
//     return () => {
//       if (debounceTimeout.current) {
//         clearTimeout(debounceTimeout.current);
//       }
//     };
//   }, []);

//   // Fetch cart when component mounts
//   useEffect(() => {
//     const fetchCart = async () => {
//       try {
//         await dispatch(fetchCartThunk());
//       } catch (error) {
//         console.error("Failed to fetch cart:", error);
//       }
//     };

//     fetchCart();
//   }, [dispatch]);

//   const subtotal = calculateSubtotal();
//   const shippingCharges = cart.length > 0 ? 2.5 : 0;
//   const taxes = cart.length > 0 ? subtotal * 0.05 : 0;
//   const total = cart.length > 0 ? subtotal + shippingCharges : 0;

//   const handleContinue = () => {
//     setShowShippingAddress(true);
//   };

//   const handleShippingSubmit = (address) => {
//     console.log("Shipping Address:", address);
//     setShowShippingAddress(false);
//   };

//   const cartItemCount = cart.reduce((total, item) => total + item.quantity, 0);

//   const renderQuantityControls = (item) => {
//     return (
//       <div className="flex items-center justify-center space-x-2">
//         <button
//           className={`w-8 h-8 flex items-center justify-center rounded-md transition-colors ${
//             item.quantity <= 1
//               ? "bg-gray-100 text-gray-400 cursor-not-allowed"
//               : "bg-gray-200 hover:bg-gray-300"
//           }`}
//           onClick={(e) => {
//             e.stopPropagation();
//             handleQuantityChange(item?.productId?._id, item?.quantity - 1);
//           }}
//           disabled={item.quantity <= 1}
//         >
//           <span className="text-lg">−</span>
//         </button>
//         <span className="w-8 text-center">
//           {item?.quantity}
//         </span>
//         <button
//           className="w-8 h-8 flex items-center justify-center bg-gray-200 rounded-md hover:bg-gray-300 transition-colors"
//           onClick={(e) => {
//             e.stopPropagation();
//             handleQuantityChange(item?.productId?._id, item?.quantity + 1);
//           }}
//         >
//           <span className="text-lg">+</span>
//         </button>
//       </div>
//     );
//   };

//   if (loading) {
//     return (
//       <>
//         <Header />
//         <div className="w-full md:w-[96vw] mx-auto p-4 md:p-6 bg-gray-100 mt-8">
//           <div className="flex justify-center items-center h-64">
//             <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
//           </div>
//         </div>
//       </>
//     );
//   }

//   if (error) {
//     return (
//       <>
//         <Header />
//         <div className="w-full md:w-[96vw] mx-auto p-4 md:p-6 bg-gray-100 mt-8">
//           <div className="bg-white p-4 rounded-lg shadow text-center">
//             <p className="text-red-500 mb-4">Error loading cart: {error}</p>
//             <button 
//               onClick={() => dispatch(fetchCartThunk())}
//               className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
//             >
//               Retry
//             </button>
//           </div>
//         </div>
//       </>
//     );
//   }

//   return (
//     <>
//       <Header/>
//       <div className="w-full md:w-[96vw] mx-auto p-4 md:p-6 bg-gray-100 mt-8">
//         <h1 className="text-xl md:text-2xl font-bold mb-4 md:mb-6 flex items-center">
//           <span className="text-xl text-blue-500 mr-2">🛒</span> Cart
//           {cartItemCount > 0 && (
//             <span className="ml-2 inline-flex items-center justify-center w-6 h-6 text-xs font-semibold text-white bg-red-600 rounded-full">
//               {cartItemCount}
//             </span>
//           )}
//         </h1>
        
//         <div className="flex flex-col lg:flex-row gap-4 md:gap-8">
//           {/* Cart Items Table */}
//           <div className="lg:w-2/3">
//             <div className="bg-white rounded-lg shadow overflow-hidden">
//               {cart.length === 0 ? (
//                 <div className="text-center py-12">
//                   <p className="text-gray-500 text-lg">Your cart is empty</p>
//                 </div>
//               ) : (
//                 <>
//                   {/* Desktop Table */}
//                   <div className="hidden md:block">
//                     <table className="w-full">
//                       <thead className="bg-gray-50">
//                         <tr>
//                           <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
//                             Product
//                           </th>
//                           <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
//                             Quantity
//                           </th>
//                           <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
//                             Subtotal
//                           </th>
//                           <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
//                             Actions
//                           </th>
//                         </tr>
//                       </thead>
//                       <tbody className="divide-y divide-gray-200">
//                         {cart.map((item) => {
//                           const price = parsePrice(item?.price);
//                           return (
//                             <tr key={item?.productId?._id}>
//                               <td className="px-6 py-4 whitespace-nowrap">
//                                 <div className="flex items-center">
//                                   <div className="flex-shrink-0 h-20 w-20">
//                                     <img
//                                       className="h-20 w-20 object-cover rounded"
//                                       src={item.image}
//                                       alt={item.title}
//                                     />
//                                   </div>
//                                   <div className="ml-4">
//                                     <div className="text-sm font-medium text-gray-900">
//                                       {item?.name}
//                                     </div>
//                                     <div className="text-sm text-gray-500">
//                                       {item?.price?.toFixed(2)} Rs
//                                     </div>
//                                   </div>
//                                 </div>
//                               </td>
//                               <td className="px-6 py-4 whitespace-nowrap">
//                                 {renderQuantityControls(item)}
//                               </td>
//                               <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
//                                 {(price * item?.quantity)?.toFixed(2)} Rs
//                               </td>
//                               <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
//                                 <button
//                                   onClick={() => handleRemove(item?.productId?._id)}
//                                   className="text-red-600 hover:text-red-900 transition-colors"
//                                 >
//                                   <span className="text-lg"><RiDeleteBin6Line/></span>
//                                 </button>
//                               </td>
//                             </tr>
//                           );
//                         })}
//                       </tbody>
//                     </table>
//                   </div>

//                   {/* Mobile List */}
//                   <div className="md:hidden">
//                     {cart.map((item) => {
//                       const price = parsePrice(item?.price);
//                       return (
//                         <div key={item.id} className="p-4 border-b border-gray-200">
//                           <div className="flex justify-between items-start">
//                             <div className="flex items-center">
//                               <div className="flex-shrink-0 h-16 w-16">
//                                 <img
//                                   className="h-16 w-16 object-cover rounded"
//                                   src={item.image}
//                                   alt={item.title}
//                                 />
//                               </div>
//                               <div className="ml-3">
//                                 <div className="text-sm font-medium text-gray-900">
//                                   {item?.name}
//                                 </div>
//                                 <div className="text-sm text-gray-500">
//                                   {price?.toFixed(2)} Rs
//                                 </div>
//                               </div>
//                             </div>
//                             <button
//                               onClick={() => handleRemove(item.id)}
//                               className="text-red-600 hover:text-red-900 transition-colors"
//                             >
//                               <RiDeleteBin6Line size={18} />
//                             </button>
//                           </div>
                          
//                           <div className="mt-3 flex justify-between items-center">
//                             {renderQuantityControls(item)}
//                             <div className="text-sm font-medium">
//                               Subtotal: {(price * item?.quantity)?.toFixed(2)} Rs
//                             </div>
//                           </div>
//                         </div>
//                       );
//                     })}
//                   </div>
//                 </>
//               )}
//             </div>
//           </div>

//           {/* Order Summary */}
//           <div className="lg:w-1/3">
//             <div className="bg-white rounded-lg shadow p-4 md:p-6">
//               <h2 className="text-lg font-semibold mb-4">Order Summary</h2>
//               <div className="space-y-2 mb-4">
//                 <div className="flex justify-between text-sm">
//                   <span>Subtotal</span>
//                   <span>{subtotal?.toFixed(2)} Rs</span>
//                 </div>
//                 <div className="flex justify-between text-sm">
//                   <span>Shipping Charges</span>
//                   <span>{shippingCharges?.toFixed(2)} Rs</span>
//                 </div>
//                 <div className="flex justify-between font-semibold text-lg pt-2 border-t">
//                   <span>Total</span>
//                   <span>{total?.toFixed(2)} Rs</span>
//                 </div>
//               </div>
              
//               <button
//                 className={`w-full py-2 px-4 rounded-md transition duration-200 mb-4 ${
//                   cart.length === 0
//                     ? "bg-gray-400 text-gray-200 cursor-not-allowed"
//                     : "bg-blue-500 text-white hover:bg-blue-600"
//                 }`}
//                 onClick={handleContinue}
//                 disabled={cart?.length === 0}
//               >
//                 Continue
//               </button>
              
//               <div className="flex items-center mb-2">
//                 <input
//                   type="checkbox"
//                   id="useDiscount"
//                   className="mr-2"
//                   checked={showCouponInput}
//                   onChange={() => setShowCouponInput(!showCouponInput)}
//                   disabled={cart.length === 0}
//                 />
//                 <label 
//                   htmlFor="useDiscount" 
//                   className={`text-sm ${cart.length === 0 ? "text-gray-400" : "text-gray-600"}`}
//                 >
//                   Use discount code
//                 </label>
//               </div>
              
//               {showCouponInput && (
//                 <div className="flex space-x-2">
//                   <input
//                     type="text"
//                     placeholder="Enter code"
//                     value={couponCode}
//                     onChange={(e) => setCouponCode(e.target.value)}
//                     className="flex-grow px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
//                   />
//                   <button
//                     onClick={applyDiscount}
//                     className="bg-blue-500 text-white px-4 py-2 rounded-md hover:bg-blue-600 transition duration-200"
//                   >
//                     Apply
//                   </button>
//                 </div>
//               )}
//             </div>
//           </div>
//         </div>

//         {showShippingAddress && (
//           <ShippingAddress
//             onClose={() => setShowShippingAddress(false)}
//             onSubmit={handleShippingSubmit}
//             orderSummary={{
//               subtotal,
//               shippingCharges,
//               taxes,
//               appliedDiscount,
//               total,
//             }}
//             clearCart={() => dispatch(clearCartThunk())}
//           />
//         )}
//       </div>
//     </>
//   );
// };

// export default CartPage;

// import React, { useState, useEffect, useCallback, useRef } from "react";
// import { useDispatch, useSelector } from "react-redux";
// import { 
//   fetchCart as fetchCartThunk,
//   removeFromCart as removeFromCartThunk,
//    updateItemQuantity,
//   clearCart as clearCartThunk
// } from "../redux/thunks/cartThunks";
// // import { setCartItems } from "../redux/slices/";
// import ShippingAddress from "../Page/SubHeaderPage/Grocery/ShippingAddress";
// import { RiDeleteBin6Line } from "react-icons/ri";
// import Header from "../Page/Header/Header";
// import Cookies from "js-cookie";

// const CartPage = () => {
//   const cart = useSelector(state => state.cart.items);
//   const { loading, error } = useSelector(state => state.cart);
//   const dispatch = useDispatch();
  
//   const [couponCode, setCouponCode] = useState("");
//   const [showCouponInput, setShowCouponInput] = useState(false);
//   const [appliedDiscount, setAppliedDiscount] = useState(0);
//   const [showShippingAddress, setShowShippingAddress] = useState(false);
//   const debounceTimeout = useRef(null);

//   // Function to parse price correctly
//   const parsePrice = (price) => {
//     if (typeof price === "number") return price;
    
//     if (typeof price === "string") {
//       const cleanedPrice = price.replace("₹", "").trim();
//       const parsedPrice = parseFloat(cleanedPrice) || 0;
//       return isNaN(parsedPrice) ? 0 : Math.max(0, parsedPrice);
//     }
    
//     return 0;
//   };

//   const calculateSubtotal = () => {
//     return cart.reduce((total, item) => {
//       const price = parsePrice(item?.price);
//       return total + price * item.quantity;
//     }, 0);
//   };

//   const applyDiscount = () => {
//     if (couponCode.toUpperCase() === "DISCOUNT10") {
//       const discount = calculateSubtotal() * 0.1;
//       setAppliedDiscount(discount);
//       alert("Discount applied successfully!");
//     } else {
//       alert("Invalid discount code");
//     }
//   };

//   // Debounced quantity change handler
//   const handleQuantityChange = useCallback(async (id, quantity, act) => {
//     // Update local state immediately for responsive UI
//     dispatch(updateItemQuantity({ productId: id, quantity }));

//     // Clear any existing timeout
//     if (debounceTimeout.current) {
//       clearTimeout(debounceTimeout.current);
//     }

//     // Set a new timeout for API call
//     debounceTimeout.current = setTimeout(async () => {
//       try {
//         await dispatch(updateItemQuantity({ productId: id, quantity }));
//       } catch (error) {
//         console.error("Error updating quantity:", error);
//         // Revert the quantity change if the API call fails
//         if (act === 'increase') {
//           dispatch(updateItemQuantity({ productId: id, quantity: quantity - 1 }));
//         } else {
//           dispatch(updateItemQuantity({ productId: id, quantity: quantity + 1 }));
//         }
//         alert("Failed to update cart item.");
//       }
//     }, 800); // 800ms delay
//   }, [dispatch]);

//   const handleRemove = async (id) => {
//     try {
//       await dispatch(removeFromCartThunk(id));
//     } catch (error) {
//       console.error("Error removing item:", error);
//       alert("Failed to remove item from cart.");
//     }
//   };

//   // Clean up timeout on component unmount
//   useEffect(() => {
//     return () => {
//       if (debounceTimeout.current) {
//         clearTimeout(debounceTimeout.current);
//       }
//     };
//   }, []);

//   // Fetch cart when component mounts
//   useEffect(() => {
//     const fetchCart = async () => {
//       try {
//         await dispatch(fetchCartThunk());
//       } catch (error) {
//         console.error("Failed to fetch cart:", error);
//       }
//     };

//     fetchCart();
//   }, [dispatch]);

//   const subtotal = calculateSubtotal();
//   const shippingCharges = cart.length > 0 ? 2.5 : 0;
//   const taxes = cart.length > 0 ? subtotal * 0.05 : 0;
//   const total = cart.length > 0 ? subtotal + shippingCharges : 0;

//   const handleContinue = () => {
//     setShowShippingAddress(true);
//   };

//   const handleShippingSubmit = (address) => {
//     console.log("Shipping Address:", address);
//     setShowShippingAddress(false);
//   };

//   const cartItemCount = cart.reduce((total, item) => total + item.quantity, 0);

//   if (loading) {
//     return (
//       <>
//         <Header />
//         <div className="w-full md:w-[96vw] mx-auto p-4 md:p-6 bg-gray-100 mt-8">
//           <div className="flex justify-center items-center h-64">
//             <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
//           </div>
//         </div>
//       </>
//     );
//   }

//   if (error) {
//     return (
//       <>
//         <Header />
//         <div className="w-full md:w-[96vw] mx-auto p-4 md:p-6 bg-gray-100 mt-8">
//           <div className="bg-white p-4 rounded-lg shadow text-center">
//             <p className="text-red-500 mb-4">Error loading cart: {error}</p>
//             <button 
//               onClick={() => dispatch(fetchCartThunk())}
//               className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
//             >
//               Retry
//             </button>
//           </div>
//         </div>
//       </>
//     );
//   }

//   return (
//     <>
//       <Header/>
//       <div className="w-full md:w-[96vw] mx-auto p-4 md:p-6 bg-gray-100 mt-8">
//         <h1 className="text-xl md:text-2xl font-bold mb-4 md:mb-6 flex items-center">
//           <span className="text-xl text-blue-500 mr-2">🛒</span> Cart
//           {cartItemCount > 0 && (
//             <span className="ml-2 inline-flex items-center justify-center w-6 h-6 text-xs font-semibold text-white bg-red-600 rounded-full">
//               {cartItemCount}
//             </span>
//           )}
//         </h1>
        
//         <div className="flex flex-col lg:flex-row gap-4 md:gap-8">
//           {/* Cart Items Table */}
//           <div className="lg:w-2/3">
//             <div className="bg-white rounded-lg shadow overflow-hidden">
//               {cart.length === 0 ? (
//                 <div className="text-center py-12">
//                   <p className="text-gray-500 text-lg">Your cart is empty</p>
//                 </div>
//               ) : (
//                 <>
//                   {/* Desktop Table */}
//                   <div className="hidden md:block">
//                     <table className="w-full">
//                       <thead className="bg-gray-50">
//                         <tr>
//                           <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
//                             Product
//                           </th>
//                           <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
//                             Quantity
//                           </th>
//                           <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
//                             Subtotal
//                           </th>
//                           <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
//                             Actions
//                           </th>
//                         </tr>
//                       </thead>
//                       <tbody className="divide-y divide-gray-200">
//                         {cart.map((item) => {
//                           const price = parsePrice(item?.price);
//                           return (
//                             <tr key={item?.productId?._id}>
//                               <td className="px-6 py-4 whitespace-nowrap">
//                                 <div className="flex items-center">
//                                   <div className="flex-shrink-0 h-20 w-20">
//                                     <img
//                                       className="h-20 w-20 object-cover rounded"
//                                       src={item.image}
//                                       alt={item.title}
//                                     />
//                                   </div>
//                                   <div className="ml-4">
//                                     <div className="text-sm font-medium text-gray-900">
//                                       {item?.name}
//                                     </div>
//                                     <div className="text-sm text-gray-500">
//                                       {item?.price?.toFixed(2)} Rs
//                                     </div>
//                                   </div>
//                                 </div>
//                               </td>
//                               <td className="px-6 py-4 whitespace-nowrap">
//                                 <div className="flex items-center justify-center space-x-2">
//                                   <button
//                                     className={`w-8 h-8 flex items-center justify-center rounded-md transition-colors ${
//                                       item.quantity <= 1
//                                         ? "bg-gray-100 text-gray-400"
//                                         : "bg-gray-200 hover:bg-gray-300"
//                                     }`}
//                                     onClick={() => 
//                                       handleQuantityChange(item?.productId?._id, item?.quantity - 1, 'decrease')
//                                     }
//                                     disabled={item.quantity <= 1}
//                                   >
//                                     <span className="text-lg">−</span>
//                                   </button>
//                                   <span className="w-8 text-center">
//                                     {item?.quantity}
//                                   </span>
//                                   <button
//                                     className="w-8 h-8 flex items-center justify-center bg-gray-200 rounded-md hover:bg-gray-300 transition-colors"
//                                     onClick={() => 
//                                       handleQuantityChange(item?.productId?._id, item?.quantity + 1, 'increase')
//                                     }
//                                   >
//                                     <span className="text-lg">+</span>
//                                   </button>
//                                 </div>
//                               </td>
//                               <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
//                                 {(price * item?.quantity)?.toFixed(2)} Rs
//                               </td>
//                               <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
//                                 <button
//                                   onClick={() => handleRemove(item?.productId?._id)}
//                                   className="text-red-600 hover:text-red-900 transition-colors"
//                                 >
//                                   <span className="text-lg"><RiDeleteBin6Line/></span>
//                                 </button>
//                               </td>
//                             </tr>
//                           );
//                         })}
//                       </tbody>
//                     </table>
//                   </div>

//                   {/* Mobile List */}
//                   <div className="md:hidden">
//                     {cart.map((item) => {
//                       const price = parsePrice(item?.price);
//                       return (
//                         <div key={item.id} className="p-4 border-b border-gray-200">
//                           <div className="flex justify-between items-start">
//                             <div className="flex items-center">
//                               <div className="flex-shrink-0 h-16 w-16">
//                                 <img
//                                   className="h-16 w-16 object-cover rounded"
//                                   src={item.image}
//                                   alt={item.title}
//                                 />
//                               </div>
//                               <div className="ml-3">
//                                 <div className="text-sm font-medium text-gray-900">
//                                   {item?.name}
//                                 </div>
//                                 <div className="text-sm text-gray-500">
//                                   {price?.toFixed(2)} Rs
//                                 </div>
//                               </div>
//                             </div>
//                             <button
//                               onClick={() => handleRemove(item.id)}
//                               className="text-red-600 hover:text-red-900 transition-colors"
//                             >
//                               <RiDeleteBin6Line size={18} />
//                             </button>
//                           </div>
                          
//                           <div className="mt-3 flex justify-between items-center">
//                             <div className="flex items-center space-x-2">
//                               <button
//                                 className={`w-8 h-8 flex items-center justify-center rounded-md transition-colors ${
//                                   item.quantity <= 1
//                                     ? "bg-gray-100 text-gray-400"
//                                     : "bg-gray-200 hover:bg-gray-300"
//                                 }`}
//                                 onClick={() => 
//                                   handleQuantityChange(item.id, item?.quantity - 1, 'decrease')
//                                 }
//                                 disabled={item.quantity <= 1}
//                               >
//                                 <span className="text-lg">−</span>
//                               </button>
//                               <span className="w-8 text-center">
//                                 {item?.quantity}
//                               </span>
//                               <button
//                                 className="w-8 h-8 flex items-center justify-center bg-gray-200 rounded-md hover:bg-gray-300 transition-colors"
//                                 onClick={() => 
//                                   handleQuantityChange(item.id, item?.quantity + 1, 'increase')
//                                 }
//                               >
//                                 <span className="text-lg">+</span>
//                               </button>
//                             </div>
//                             <div className="text-sm font-medium">
//                               Subtotal: {(price * item?.quantity)?.toFixed(2)} Rs
//                             </div>
//                           </div>
//                         </div>
//                       );
//                     })}
//                   </div>
//                 </>
//               )}
//             </div>
//           </div>

//           {/* Order Summary */}
//           <div className="lg:w-1/3">
//             <div className="bg-white rounded-lg shadow p-4 md:p-6">
//               <h2 className="text-lg font-semibold mb-4">Order Summary</h2>
//               <div className="space-y-2 mb-4">
//                 <div className="flex justify-between text-sm">
//                   <span>Subtotal</span>
//                   <span>{subtotal?.toFixed(2)} Rs</span>
//                 </div>
//                 <div className="flex justify-between text-sm">
//                   <span>Shipping Charges</span>
//                   <span>{shippingCharges?.toFixed(2)} Rs</span>
//                 </div>
//                 <div className="flex justify-between font-semibold text-lg pt-2 border-t">
//                   <span>Total</span>
//                   <span>{total?.toFixed(2)} Rs</span>
//                 </div>
//               </div>
              
//               <button
//                 className={`w-full py-2 px-4 rounded-md transition duration-200 mb-4 ${
//                   cart.length === 0
//                     ? "bg-gray-400 text-gray-200 cursor-not-allowed"
//                     : "bg-blue-500 text-white hover:bg-blue-600"
//                 }`}
//                 onClick={handleContinue}
//                 disabled={cart?.length === 0}
//               >
//                 Continue
//               </button>
              
//               <div className="flex items-center mb-2">
//                 <input
//                   type="checkbox"
//                   id="useDiscount"
//                   className="mr-2"
//                   checked={showCouponInput}
//                   onChange={() => setShowCouponInput(!showCouponInput)}
//                   disabled={cart.length === 0}
//                 />
//                 <label 
//                   htmlFor="useDiscount" 
//                   className={`text-sm ${cart.length === 0 ? "text-gray-400" : "text-gray-600"}`}
//                 >
//                   Use discount code
//                 </label>
//               </div>
              
//               {showCouponInput && (
//                 <div className="flex space-x-2">
//                   <input
//                     type="text"
//                     placeholder="Enter code"
//                     value={couponCode}
//                     onChange={(e) => setCouponCode(e.target.value)}
//                     className="flex-grow px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
//                   />
//                   <button
//                     onClick={applyDiscount}
//                     className="bg-blue-500 text-white px-4 py-2 rounded-md hover:bg-blue-600 transition duration-200"
//                   >
//                     Apply
//                   </button>
//                 </div>
//               )}
//             </div>
//           </div>
//         </div>

//         {showShippingAddress && (
//           <ShippingAddress
//             onClose={() => setShowShippingAddress(false)}
//             onSubmit={handleShippingSubmit}
//             orderSummary={{
//               subtotal,
//               shippingCharges,
//               taxes,
//               appliedDiscount,
//               total,
//             }}
//             clearCart={() => dispatch(clearCartThunk())}
//           />
//         )}
//       </div>
//     </>
//   );
// };

// export default CartPage;

// import React, { useState,useEffect } from "react";
// import { useDispatch, useSelector } from "react-redux";
// import { removeFromCart, updateQuantity, setCartItems } from "../redux/slices/cartSlice";
// import ShippingAddress from "../Page/SubHeaderPage/Grocery/ShippingAddress";
// import { RiDeleteBin6Line } from "react-icons/ri";
// import Header from "../Page/Header/Header";
// import axios from "axios";
// import Cookies from "js-cookie";

// const CartPage = () => {
//   const cart = useSelector(state => state.cart.items);
//   const dispatch = useDispatch();
  
//   const [couponCode, setCouponCode] = useState("");
//   const [showCouponInput, setShowCouponInput] = useState(false);
//   const [appliedDiscount, setAppliedDiscount] = useState(0);
//   const [showShippingAddress, setShowShippingAddress] = useState(false);

//   // Function to parse price correctly
//   const parsePrice = (price) => {
//     if (typeof price === "number") return price;
    
//     if (typeof price === "string") {
//       const cleanedPrice = price.replace("₹", "").trim();
//       const parsedPrice = parseFloat(cleanedPrice) || 0;
//       return isNaN(parsedPrice) ? 0 : Math.max(0, parsedPrice);
//     }
    
//     return 0;
//   };

//   const calculateSubtotal = () => {
//     return cart.reduce((total, item) => {
//       const price = parsePrice(item?.price);
//       return total + price * item.quantity;
//     }, 0);
//   };

//   const applyDiscount = () => {
//     if (couponCode.toUpperCase() === "DISCOUNT10") {
//       const discount = calculateSubtotal() * 0.1;
//       setAppliedDiscount(discount);
//       alert("Discount applied successfully!");
//     } else {
//       alert("Invalid discount code");
//     }
//   };

//   useEffect(() => {
//     const fetchCart = async () => {
//       const token = Cookies.get("ecom_user_token");
//       try {
//         const response = await axios.get("https://seller-backend-29o9.onrender.com/api/buyer/get-cart", {
//           headers: {
//             Authorization: `Bearer ${token}`,
//           },
//         });

//         const items = response.data.cart.items.map((item) => ({
//           id: item.productId._id,
//           name: item.productId.name,
//           price: item.price,
//           quantity: item.quantity,
//           image: item.productId.image || "/default-product.jpg", // fallback image
//         }));

//         dispatch(setCartItems(items));
//       } catch (error) {
//         console.error("Failed to fetch cart:", error);
//       }
//     };

//     fetchCart();
//   }, [dispatch]);


//   const handleQuantityChange = async (id, quantity, act) => {
//     const token = Cookies.get("ecom_user_token");
    
//     // If quantity is 0 or less, remove the item
//     if (quantity <= 0) {
//       try {
//         await axios.patch(
//           "https://seller-backend-29o9.onrender.com/api/buyer/remove-from-cart",
//           {
//             productId: id,
//           },
//           {
//             headers: {
//               Authorization: `Bearer ${token}`,
//             },
//           }
//         );
//         dispatch(removeFromCart(id));
//         return; // Exit the function after removal
//       } catch (error) {
//         console.error("Error removing item:", error);
//         alert("Failed to remove item from cart.");
//         return;
//       }
//     }

//     try {
//       await axios.post(
//         "https://seller-backend-29o9.onrender.com/api/buyer/add-to-cart",
//         {
//           productId: id,
//           quantity: quantity,
//         },
//         {
//           headers: {
//             Authorization: `Bearer ${token}`,
//           },
//         }
//       );

//       // Update local state with the new quantity
//       dispatch(updateQuantity({ id, quantity }));
//     } catch (error) {
//       console.error("Error updating quantity:", error);
//       alert("Failed to update cart item.");
//     }
//   };

//   const handleRemove = async (id) => {
//     const token = Cookies.get("ecom_user_token");

//     try {
//       await axios.patch(
//         "https://seller-backend-29o9.onrender.com/api/buyer/remove-from-cart",
//         {
//           productId: id,
//         },
//         {
//           headers: {
//             Authorization: `Bearer ${token}`,
//           },
//         }
//       );

//       dispatch(removeFromCart(id));
//     } catch (error) {
//       console.error("Error removing item:", error);
//       alert("Failed to remove item from cart.");
//     }
//   };

//   const subtotal = calculateSubtotal();
//   const shippingCharges = cart.length > 0 ? 2.5 : 0;
//   const taxes = cart.length > 0 ? subtotal * 0.05 : 0;
//   const total = cart.length > 0 ? subtotal + shippingCharges + taxes - appliedDiscount : 0;

//   const handleContinue = () => {
//     setShowShippingAddress(true);
//   };

//   const handleShippingSubmit = (address) => {
//     console.log("Shipping Address:", address);
//     setShowShippingAddress(false);
//   };

//   const cartItemCount = cart.reduce((total, item) => total + item.quantity, 0);
// import React, { useState, useEffect, useCallback, useRef } from "react";
// import { useDispatch, useSelector } from "react-redux";
// import { removeFromCart, updateQuantity, setCartItems } from "../redux/slices/cartSlice";
// import ShippingAddress from "../Page/SubHeaderPage/Grocery/ShippingAddress";
// import { RiDeleteBin6Line } from "react-icons/ri";
// import Header from "../Page/Header/Header";
// import axios from "axios";
// import Cookies from "js-cookie";

// const CartPage = () => {
//   const cart = useSelector(state => state.cart.items);
//   const dispatch = useDispatch();
  
//   const [couponCode, setCouponCode] = useState("");
//   const [showCouponInput, setShowCouponInput] = useState(false);
//   const [appliedDiscount, setAppliedDiscount] = useState(0);
//   const [showShippingAddress, setShowShippingAddress] = useState(false);
//   const debounceTimeout = useRef(null);

//   // Function to parse price correctly
//   const parsePrice = (price) => {
//     if (typeof price === "number") return price;
    
//     if (typeof price === "string") {
//       const cleanedPrice = price.replace("₹", "").trim();
//       const parsedPrice = parseFloat(cleanedPrice) || 0;
//       return isNaN(parsedPrice) ? 0 : Math.max(0, parsedPrice);
//     }
    
//     return 0;
//   };

//   const calculateSubtotal = () => {
//     return cart.reduce((total, item) => {
//       const price = parsePrice(item?.price);
//       return total + price * item.quantity;
//     }, 0);
//   };

//   const applyDiscount = () => {
//     if (couponCode.toUpperCase() === "DISCOUNT10") {
//       const discount = calculateSubtotal() * 0.1;
//       setAppliedDiscount(discount);
//       alert("Discount applied successfully!");
//     } else {
//       alert("Invalid discount code");
//     }
//   };

//   useEffect(() => {
//     const fetchCart = async () => {
//       const token = Cookies.get("ecom_user_token");
//       try {
//         const response = await axios.get("https://seller-backend-29o9.onrender.com/api/buyer/get-cart", {
//           headers: {
//             Authorization: `Bearer ${token}`,
//           },
//         });

//         const items = response.data.cart.items.map((item) => ({
//           id: item.productId._id,
//           name: item.productId.name,
//           price: item.price,
//           quantity: item.quantity,
//           image: item.productId.image || "/default-product.jpg", // fallback image
//         }));

//         dispatch(setCartItems(items));
//       } catch (error) {
//         console.error("Failed to fetch cart:", error);
//       }
//     };

//     fetchCart();
//   }, [dispatch]);

//   // Debounced quantity change handler
//   const handleQuantityChange = useCallback(async (id, quantity, act) => {
//     // Update local state immediately for responsive UI
//     dispatch(updateQuantity({ id, quantity }));

//     // Clear any existing timeout
//     if (debounceTimeout.current) {
//       clearTimeout(debounceTimeout.current);
//     }

//     // Set a new timeout for API call
//     debounceTimeout.current = setTimeout(async () => {
//       const token = Cookies.get("ecom_user_token");
      
//       // If quantity is 0 or less, remove the item
//       if (quantity <= 0) {
//         try {
//           await axios.patch(
//             "https://seller-backend-29o9.onrender.com/api/buyer/remove-from-cart",
//             {
//               productId: id,
//             },
//             {
//               headers: {
//                 Authorization: `Bearer ${token}`,
//               },
//             }
//           );
//           dispatch(removeFromCart(id));
//           return;
//         } catch (error) {
//           console.error("Error removing item:", error);
//           alert("Failed to remove item from cart.");
//           return;
//         }
//       }

//       try {
//         await axios.post(
//           "https://seller-backend-29o9.onrender.com/api/buyer/add-to-cart",
//           {
//             productId: id,
//             quantity: quantity,
//           },
//           {
//             headers: {
//               Authorization: `Bearer ${token}`,
//             },
//           }
//         );
//       } catch (error) {
//         console.error("Error updating quantity:", error);
//         alert("Failed to update cart item.");
//       }
//     }, 500); // 500ms delay
//   }, [dispatch]);

//   const handleRemove = async (id) => {
//     const token = Cookies.get("ecom_user_token");

//     try {
//       await axios.patch(
//         "https://seller-backend-29o9.onrender.com/api/buyer/remove-from-cart",
//         {
//           productId: id,
//         },
//         {
//           headers: {
//             Authorization: `Bearer ${token}`,
//           },
//         }
//       );

//       dispatch(removeFromCart(id));
//     } catch (error) {
//       console.error("Error removing item:", error);
//       alert("Failed to remove item from cart.");
//     }
//   };

//   // Clean up timeout on component unmount
//   useEffect(() => {
//     return () => {
//       if (debounceTimeout.current) {
//         clearTimeout(debounceTimeout.current);
//       }
//     };
//   }, []);

//   const subtotal = calculateSubtotal();
//   const shippingCharges = cart.length > 0 ? 2.5 : 0;
//   const taxes = cart.length > 0 ? subtotal * 0.05 : 0;
//   const total = cart.length > 0 ? subtotal + shippingCharges + taxes - appliedDiscount : 0;

//   const handleContinue = () => {
//     setShowShippingAddress(true);
//   };

//   const handleShippingSubmit = (address) => {
//     console.log("Shipping Address:", address);
//     setShowShippingAddress(false);
//   };

//   const cartItemCount = cart.reduce((total, item) => total + item.quantity, 0);

// import React, { useState, useEffect, useCallback, useRef } from "react";
// import { useDispatch, useSelector } from "react-redux";
// import { clearCart,removeFromCart, updateQuantity, setCartItems } from "../redux/slices/cartSlice";
// import ShippingAddress from "../Page/SubHeaderPage/Grocery/ShippingAddress";
// import { RiDeleteBin6Line } from "react-icons/ri";
// import Header from "../Page/Header/Header";
// import axios from "axios";
// import Cookies from "js-cookie";

// const CartPage = () => {
//   const cart = useSelector(state => state.cart.items);
//   const dispatch = useDispatch();
  
//   const [couponCode, setCouponCode] = useState("");
//   const [showCouponInput, setShowCouponInput] = useState(false);
//   const [appliedDiscount, setAppliedDiscount] = useState(0);
//   const [showShippingAddress, setShowShippingAddress] = useState(false);
//   const debounceTimeout = useRef(null);

//   // Function to parse price correctly
//   const parsePrice = (price) => {
//     if (typeof price === "number") return price;
    
//     if (typeof price === "string") {
//       const cleanedPrice = price.replace("₹", "").trim();
//       const parsedPrice = parseFloat(cleanedPrice) || 0;
//       return isNaN(parsedPrice) ? 0 : Math.max(0, parsedPrice);
//     }
    
//     return 0;
//   };

//   const calculateSubtotal = () => {
//     return cart.reduce((total, item) => {
//       const price = parsePrice(item?.price);
//       return total + price * item.quantity;
//     }, 0);
//   };

//   const applyDiscount = () => {
//     if (couponCode.toUpperCase() === "DISCOUNT10") {
//       const discount = calculateSubtotal() * 0.1;
//       setAppliedDiscount(discount);
//       alert("Discount applied successfully!");
//     } else {
//       alert("Invalid discount code");
//     }
//   };


//    const fetchCart = async () => {
//       const token = Cookies.get("ecom_user_token");
//       try {
//         const response = await axios.get("https://seller-backend-29o9.onrender.com/api/buyer/get-cart", {
//           headers: {
//             Authorization: `Bearer ${token}`,
//           },
//         });

//         const items = response.data.cart.items.map((item) => ({
//           id: item.productId._id,
//           name: item.productId.name,
//           price: item.price,
//           quantity: item.quantity,
//           image: item.productId.image || "/default-product.jpg",
//         }));

//         dispatch(setCartItems(items));
//       } catch (error) {
//         console.error("Failed to fetch cart:", error);
//       }
//     };

//   useEffect(() => {
   
//     fetchCart();
//   }, [dispatch]);

//   // Debounced quantity change handler
//   const handleQuantityChange = useCallback(async (id, quantity, act) => {
//     // Update local state immediately for responsive UI
//     dispatch(updateQuantity({ id, quantity }));

//     // Clear any existing timeout
//     if (debounceTimeout.current) {
//       clearTimeout(debounceTimeout.current);
//     }

//     // Set a new timeout for API call
//     debounceTimeout.current = setTimeout(async () => {
//       const token = Cookies.get("ecom_user_token");
      
//       // If quantity is 0 or less, remove the item
//       if (quantity <= 0) {
//         try {
//           await axios.patch(
//             "https://seller-backend-29o9.onrender.com/api/buyer/remove-from-cart",
//             { productId: id },
//             { headers: { Authorization: `Bearer ${token}` } }
//           );
//           dispatch(removeFromCart(id));
//           return;
//         } catch (error) {
//           console.error("Error removing item:", error);
//           dispatch(updateQuantity({ id, quantity: quantity + 1 }));
//           alert("Failed to remove item from cart.");
//           return;
//         }
//       }

//       try {
//         await axios.post(
//           "https://seller-backend-29o9.onrender.com/api/buyer/add-to-cart",
//           { productId: id, quantity },
//           { headers: { Authorization: `Bearer ${token}` } }
//         );
//       } catch (error) {
//         console.error("Error updating quantity:", error);
//         if (act === 'increase') {
//           dispatch(updateQuantity({ id, quantity: quantity - 1 }));
//         } else {
//           dispatch(updateQuantity({ id, quantity: quantity + 1 }));
//         }
//         alert("Failed to update cart item.");
//       }
//     }, 800); // 500ms delay
//   }, [dispatch]);

//   const handleRemove = async (id) => {
//     const token = Cookies.get("ecom_user_token");

//     try {
//       await axios.patch(
//         "https://seller-backend-29o9.onrender.com/api/buyer/remove-from-cart",
//         { productId: id },
//         { headers: { Authorization: `Bearer ${token}` } }
//       );

//       dispatch(removeFromCart(id));
//     } catch (error) {
//       console.error("Error removing item:", error);
//       alert("Failed to remove item from cart.");
//     }
//   };

//   // Clean up timeout on component unmount
//   useEffect(() => {
//     return () => {
//       if (debounceTimeout.current) {
//         clearTimeout(debounceTimeout.current);
//       }
//     };
//   }, []);

//   const subtotal = calculateSubtotal();
//   const shippingCharges = cart.length > 0 ? 2.5 : 0;
//   const taxes = cart.length > 0 ? subtotal * 0.05 : 0;
//   // const total = cart.length > 0 ? subtotal + shippingCharges + taxes - appliedDiscount : 0;
//     const total = cart.length > 0 ? subtotal + shippingCharges : 0;


//   const handleContinue = () => {
//     setShowShippingAddress(true);
//   };

//   const handleShippingSubmit = (address) => {
//     console.log("Shipping Address:", address);
//     setShowShippingAddress(false);
//   };

//   const cartItemCount = cart.reduce((total, item) => total + item.quantity, 0);

//   return (
//     <>
//       <Header/>
//       <div className="w-full md:w-[96vw] mx-auto p-4 md:p-6 bg-gray-100 mt-8">
//         <h1 className="text-xl md:text-2xl font-bold mb-4 md:mb-6 flex items-center">
//           <span className="text-xl text-blue-500 mr-2">🛒</span> Cart
//           {cartItemCount > 0 && (
//             <span className="ml-2 inline-flex items-center justify-center w-6 h-6 text-xs font-semibold text-white bg-red-600 rounded-full">
//               {cartItemCount}
//             </span>
//           )}
//         </h1>
        
//         <div className="flex flex-col lg:flex-row gap-4 md:gap-8">
//           {/* Cart Items Table */}
//           <div className="lg:w-2/3">
//             <div className="bg-white rounded-lg shadow overflow-hidden">
//               {cart.length === 0 ? (
//                 <div className="text-center py-12">
//                   <p className="text-gray-500 text-lg">Your cart is empty</p>
//                 </div>
//               ) : (
//                 <>
//                   {/* Desktop Table */}
//                   <div className="hidden md:block">
//                     <table className="w-full">
//                       <thead className="bg-gray-50">
//                         <tr>
//                           <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
//                             Product
//                           </th>
//                           <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
//                             Quantity
//                           </th>
//                           <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
//                             Subtotal
//                           </th>
//                           <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
//                             Actions
//                           </th>
//                         </tr>
//                       </thead>
//                       <tbody className="divide-y divide-gray-200">
//                         {cart.map((item) => {
//                           const price = parsePrice(item?.price);
//                           return (
//                             <tr key={item.id}>
//                               <td className="px-6 py-4 whitespace-nowrap">
//                                 <div className="flex items-center">
//                                   <div className="flex-shrink-0 h-20 w-20">
//                                     <img
//                                       className="h-20 w-20 object-cover rounded"
//                                       src={item.image}
//                                       alt={item.title}
//                                     />
//                                   </div>
//                                   <div className="ml-4">
//                                     <div className="text-sm font-medium text-gray-900">
//                                       {item?.name}
//                                     </div>
//                                     <div className="text-sm text-gray-500">
//                                       {item?.price?.toFixed(2)} Rs
//                                     </div>
//                                   </div>
//                                 </div>
//                               </td>
//                               <td className="px-6 py-4 whitespace-nowrap">
//                                 <div className="flex items-center justify-center space-x-2">
//                                   <button
//                                     className={`w-8 h-8 flex items-center justify-center rounded-md transition-colors ${
//                                       item.quantity <= 0
//                                         ? "bg-gray-100 text-gray-400"
//                                         : "bg-gray-200 hover:bg-gray-300"
//                                     }`}
//                                     onClick={() => 
//                                       handleQuantityChange(item.id, item?.quantity - 1, 'decrease')
//                                     }
//                                   >
//                                     <span className="text-lg">−</span>
//                                   </button>
//                                   <span className="w-8 text-center">
//                                     {item?.quantity}
//                                   </span>
//                                   <button
//                                     className="w-8 h-8 flex items-center justify-center bg-gray-200 rounded-md hover:bg-gray-300 transition-colors"
//                                     onClick={() => 
//                                       handleQuantityChange(item.id, item?.quantity + 1,`increase`)
//                                     }
//                                   >
//                                     <span className="text-lg">+</span>
//                                   </button>
//                                 </div>
//                               </td>
//                               <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
//                                 {(item?.price * item?.quantity)?.toFixed(2)} Rs
//                               </td>
//                               <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
//                                 <button
//                                   onClick={() => handleRemove(item.id)}
//                                   className="text-red-600 hover:text-red-900 transition-colors"
//                                 >
//                                   <span className="text-lg"><RiDeleteBin6Line/></span>
//                                 </button>
//                               </td>
//                             </tr>
//                           );
//                         })}
//                       </tbody>
//                     </table>
//                   </div>

//                   {/* Mobile List */}
//                   <div className="md:hidden">
//                     {cart.map((item) => {
//                       const price = parsePrice(item?.price);
//                       return (
//                         <div key={item.id} className="p-4 border-b border-gray-200">
//                           <div className="flex justify-between items-start">
//                             <div className="flex items-center">
//                               <div className="flex-shrink-0 h-16 w-16">
//                                 <img
//                                   className="h-16 w-16 object-cover rounded"
//                                   src={item.image}
//                                   alt={item.title}
//                                 />
//                               </div>
//                               <div className="ml-3">
//                                 <div className="text-sm font-medium text-gray-900">
//                                   {item?.name}
//                                 </div>
//                                 <div className="text-sm text-gray-500">
//                                   {item?.price?.toFixed(2)} Rs
//                                 </div>
//                               </div>
//                             </div>
//                             <button
//                               onClick={() => handleRemove(item.id)}
//                               className="text-red-600 hover:text-red-900 transition-colors"
//                             >
//                               <RiDeleteBin6Line size={18} />
//                             </button>
//                           </div>
                          
//                           <div className="mt-3 flex justify-between items-center">
//                             <div className="flex items-center space-x-2">
//                               <button
//                                 className={`w-8 h-8 flex items-center justify-center rounded-md transition-colors ${
//                                   item.quantity <= 0
//                                     ? "bg-gray-100 text-gray-400"
//                                     : "bg-gray-200 hover:bg-gray-300"
//                                 }`}
//                                 onClick={() => 
//                                   handleQuantityChange(item.id, item?.quantity - 1, 'decrease')
//                                 }
//                               >
//                                 <span className="text-lg">−</span>
//                               </button>
//                               <span className="w-8 text-center">
//                                 {item?.quantity}
//                               </span>
//                               <button
//                                 className="w-8 h-8 flex items-center justify-center bg-gray-200 rounded-md hover:bg-gray-300 transition-colors"
//                                 onClick={() => 
//                                   handleQuantityChange(item.id, item?.quantity + 1,`increase`)
//                                 }
//                               >
//                                 <span className="text-lg">+</span>
//                               </button>
//                             </div>
//                             <div className="text-sm font-medium">
//                               Subtotal: {(item?.price * item?.quantity)?.toFixed(2)} Rs
//                             </div>
//                           </div>
//                         </div>
//                       );
//                     })}
//                   </div>
//                 </>
//               )}
//             </div>
//           </div>

//           {/* Order Summary */}
//           <div className="lg:w-1/3">
//             <div className="bg-white rounded-lg shadow p-4 md:p-6">
//               <h2 className="text-lg font-semibold mb-4">Order Summary</h2>
//               <div className="space-y-2 mb-4">
//                 <div className="flex justify-between text-sm">
//                   <span>Subtotal</span>
//                   <span>{subtotal?.toFixed(2)} Rs</span>
//                 </div>
//                 <div className="flex justify-between text-sm">
//                   <span>Shipping Charges</span>
//                   <span>{shippingCharges?.toFixed(2)} Rs</span>
//                 </div>
//                 {/* <div className="flex justify-between text-sm">
//                   <span>Taxes (5%)</span>
//                   <span>{taxes?.toFixed(2)} Rs</span>
//                 </div> */}
//                 {/* {appliedDiscount > 0 && (
//                   <div className="flex justify-between text-sm text-green-600">
//                     <span>Discount</span>
//                     <span>-{appliedDiscount?.toFixed(2)} Rs</span>
//                   </div>
//                 )} */}
//                 <div className="flex justify-between font-semibold text-lg pt-2 border-t">
//                   <span>Total</span>
//                   <span>{total?.toFixed(2)} Rs</span>
//                 </div>
//               </div>
              
//               <button
//                 className={`w-full py-2 px-4 rounded-md transition duration-200 mb-4 ${
//                   cart.length === 0
//                     ? "bg-gray-400 text-gray-200 cursor-not-allowed"
//                     : "bg-blue-500 text-white hover:bg-blue-600"
//                 }`}
//                 onClick={handleContinue}
//                 disabled={cart?.length === 0}
//               >
//                 Continue
//               </button>
              
//               <div className="flex items-center mb-2">
//                 <input
//                   type="checkbox"
//                   id="useDiscount"
//                   className="mr-2"
//                   checked={showCouponInput}
//                   onChange={() => setShowCouponInput(!showCouponInput)}
//                   disabled={cart.length === 0}
//                 />
//                 <label 
//                   htmlFor="useDiscount" 
//                   className={`text-sm ${cart.length === 0 ? "text-gray-400" : "text-gray-600"}`}
//                 >
//                   Use discount code
//                 </label>
//               </div>
              
//               {showCouponInput && (
//                 <div className="flex space-x-2">
//                   <input
//                     type="text"
//                     placeholder="Enter code"
//                     value={couponCode}
//                     onChange={(e) => setCouponCode(e.target.value)}
//                     className="flex-grow px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
//                   />
//                   <button
//                     onClick={applyDiscount}
//                     className="bg-blue-500 text-white px-4 py-2 rounded-md hover:bg-blue-600 transition duration-200"
//                   >
//                     Apply
//                   </button>
//                 </div>
//               )}
//             </div>
//           </div>
//         </div>

//         {showShippingAddress && (
//           <ShippingAddress
//             onClose={() => setShowShippingAddress(false)}
//             onSubmit={handleShippingSubmit}
//             orderSummary={{
//               subtotal,
//               shippingCharges,
//               taxes,
//               appliedDiscount,
//               total,
//             }}
//             fetchCart={fetchCart}
//             clearCart={() => dispatch(clearCart())}
//           />
//         )}
//       </div>
//     </>
//   );
// };

// export default CartPage;

// import React, { useState, useEffect, useCallback, useRef } from "react";
// import { useDispatch, useSelector } from "react-redux";
// import { removeFromCart, updateQuantity, setCartItems } from "../redux/slices/cartSlice";
// import ShippingAddress from "../Page/SubHeaderPage/Grocery/ShippingAddress";
// import { RiDeleteBin6Line } from "react-icons/ri";
// import Header from "../Page/Header/Header";
// import axios from "axios";
// import Cookies from "js-cookie";

// const CartPage = () => {
//   const cart = useSelector(state => state.cart.items);
//   const dispatch = useDispatch();
  
//   const [couponCode, setCouponCode] = useState("");
//   const [showCouponInput, setShowCouponInput] = useState(false);
//   const [appliedDiscount, setAppliedDiscount] = useState(0);
//   const [showShippingAddress, setShowShippingAddress] = useState(false);
//   const debounceTimeout = useRef(null);

//   // Function to parse price correctly
//   const parsePrice = (price) => {
//     if (typeof price === "number") return price;
    
//     if (typeof price === "string") {
//       const cleanedPrice = price.replace("₹", "").trim();
//       const parsedPrice = parseFloat(cleanedPrice) || 0;
//       return isNaN(parsedPrice) ? 0 : Math.max(0, parsedPrice);
//     }
    
//     return 0;
//   };

//   const calculateSubtotal = () => {
//     return cart.reduce((total, item) => {
//       const price = parsePrice(item?.price);
//       return total + price * item.quantity;
//     }, 0);
//   };

//   const applyDiscount = () => {
//     if (couponCode.toUpperCase() === "DISCOUNT10") {
//       const discount = calculateSubtotal() * 0.1;
//       setAppliedDiscount(discount);
//       alert("Discount applied successfully!");
//     } else {
//       alert("Invalid discount code");
//     }
//   };

//   useEffect(() => {
//     const fetchCart = async () => {
//       const token = Cookies.get("ecom_user_token");
//       try {
//         const response = await axios.get("https://seller-backend-29o9.onrender.com/api/buyer/get-cart", {
//           headers: {
//             Authorization: `Bearer ${token}`,
//           },
//         });

//         const items = response.data.cart.items.map((item) => ({
//           id: item.productId._id,
//           name: item.productId.name,
//           price: item.price,
//           quantity: item.quantity,
//           image: item.productId.image || "/default-product.jpg",
//         }));

//         dispatch(setCartItems(items));
//       } catch (error) {
//         console.error("Failed to fetch cart:", error);
//       }
//     };

//     fetchCart();
//   }, [dispatch]);

//   // Debounced quantity change handler
//   const handleQuantityChange = useCallback(async (id, quantity, act) => {
//     // Update local state immediately for responsive UI
//     dispatch(updateQuantity({ id, quantity }));

//     // Clear any existing timeout
//     if (debounceTimeout.current) {
//       clearTimeout(debounceTimeout.current);
//     }

//     // Set a new timeout for API call
//     debounceTimeout.current = setTimeout(async () => {
//       const token = Cookies.get("ecom_user_token");
      
//       // If quantity is 0 or less, remove the item
//       if (quantity <= 0) {
//         try {
//           await axios.patch(
//             "https://seller-backend-29o9.onrender.com/api/buyer/remove-from-cart",
//             { productId: id },
//             { headers: { Authorization: `Bearer ${token}` } }
//           );
//           dispatch(removeFromCart(id));
//           return;
//         } catch (error) {
//           console.error("Error removing item:", error);
//           dispatch(updateQuantity({ id, quantity: quantity + 1 }));
//           alert("Failed to remove item from cart.");
//           return;
//         }
//       }

//       try {
//         await axios.post(
//           "https://seller-backend-29o9.onrender.com/api/buyer/add-to-cart",
//           { productId: id, quantity },
//           { headers: { Authorization: `Bearer ${token}` } }
//         );
//       } catch (error) {
//         console.error("Error updating quantity:", error);
//         if (act === 'increase') {
//           dispatch(updateQuantity({ id, quantity: quantity - 1 }));
//         } else {
//           dispatch(updateQuantity({ id, quantity: quantity + 1 }));
//         }
//         alert("Failed to update cart item.");
//       }
//     }, 800); // 500ms delay
//   }, [dispatch]);

//   const handleRemove = async (id) => {
//     const token = Cookies.get("ecom_user_token");

//     try {
//       await axios.patch(
//         "https://seller-backend-29o9.onrender.com/api/buyer/remove-from-cart",
//         { productId: id },
//         { headers: { Authorization: `Bearer ${token}` } }
//       );

//       dispatch(removeFromCart(id));
//     } catch (error) {
//       console.error("Error removing item:", error);
//       alert("Failed to remove item from cart.");
//     }
//   };

//   // Clean up timeout on component unmount
//   useEffect(() => {
//     return () => {
//       if (debounceTimeout.current) {
//         clearTimeout(debounceTimeout.current);
//       }
//     };
//   }, []);

//   const subtotal = calculateSubtotal();
//   const shippingCharges = cart.length > 0 ? 2.5 : 0;
//   const taxes = cart.length > 0 ? subtotal * 0.05 : 0;
//   const total = cart.length > 0 ? subtotal + shippingCharges + taxes - appliedDiscount : 0;

//   const handleContinue = () => {
//     setShowShippingAddress(true);
//   };

//   const handleShippingSubmit = (address) => {
//     console.log("Shipping Address:", address);
//     setShowShippingAddress(false);
//   };

//   const cartItemCount = cart.reduce((total, item) => total + item.quantity, 0);

//   return (
//     <>
//       <Header/>
//       <div className="w-[96vw] mx-auto p-6 bg-gray-100 mt-8">
//         <h1 className="text-2xl font-bold mb-6 flex items-center">
//           <span className="text-xl text-blue-500 mr-2">🛒</span> Cart
//           {cartItemCount > 0 && (
//             <span className="ml-2 inline-flex items-center justify-center w-6 h-6 text-xs font-semibold text-white bg-red-600 rounded-full">
//               {cartItemCount}
//             </span>
//           )}
//         </h1>
        
//         <div className="flex flex-col lg:flex-row gap-8">
//           {/* Cart Items Table */}
//           <div className="lg:w-2/3">
//             <div className="bg-white rounded-lg shadow overflow-hidden">
//               {cart.length === 0 ? (
//                 <div className="text-center py-12">
//                   <p className="text-gray-500 text-lg">Your cart is empty</p>
//                 </div>
//               ) : (
//                 <table className="w-full">
//                   <thead className="bg-gray-50">
//                     <tr>
//                       <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
//                         Product
//                       </th>
//                       <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
//                         Quantity
//                       </th>
//                       <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
//                         Subtotal
//                       </th>
//                       <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
//                         Actions
//                       </th>
//                     </tr>
//                   </thead>
//                   <tbody className="divide-y divide-gray-200">
//                     {cart.map((item) => {
//                       const price = parsePrice(item?.price);
//                       return (
//                         <tr key={item.id}>
//                           <td className="px-6 py-4 whitespace-nowrap">
//                             <div className="flex items-center">
//                               <div className="flex-shrink-0 h-20 w-20">
//                                 <img
//                                   className="h-20 w-20 object-cover rounded"
//                                   src={item.image}
//                                   alt={item.title}
//                                 />
//                               </div>
//                               <div className="ml-4">
//                                 <div className="text-sm font-medium text-gray-900">
//                                   {item?.name}
//                                 </div>
//                                 <div className="text-sm text-gray-500">
//                                   {item?.price?.toFixed(2)} Rs
//                                 </div>
//                               </div>
//                             </div>
//                           </td>
//                           <td className="px-6 py-4 whitespace-nowrap">
//                             <div className="flex items-center justify-center space-x-2">
//                               <button
//                                 className={`w-8 h-8 flex items-center justify-center rounded-md transition-colors ${
//                                   item.quantity <= 0
//                                     ? "bg-gray-100 text-gray-400"
//                                     : "bg-gray-200 hover:bg-gray-300"
//                                 }`}
//                                 onClick={() => 
//                                   handleQuantityChange(item.id, item?.quantity - 1, 'decrease')
//                                 }
//                               >
//                                 <span className="text-lg">−</span>
//                               </button>
//                               <span className="w-8 text-center">
//                                 {item?.quantity}
//                               </span>
//                               <button
//                                 className="w-8 h-8 flex items-center justify-center bg-gray-200 rounded-md hover:bg-gray-300 transition-colors"
//                                 onClick={() => 
//                                   handleQuantityChange(item.id, item?.quantity + 1,`increase`)
//                                 }
//                               >
//                                 <span className="text-lg">+</span>
//                               </button>
//                             </div>
//                           </td>
//                           <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
//                             {(item?.price * item?.quantity)?.toFixed(2)} Rs
//                           </td>
//                           <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
//                             <button
//                               onClick={() => handleRemove(item.id)}
//                               className="text-red-600 hover:text-red-900 transition-colors"
//                             >
//                               <span className="text-lg"><RiDeleteBin6Line/></span>
//                             </button>
//                           </td>
//                         </tr>
//                       );
//                     })}
//                   </tbody>
//                 </table>
//               )}
//             </div>
//           </div>

//           {/* Order Summary */}
//           <div className="lg:w-1/3">
//             <div className="bg-white rounded-lg shadow p-6">
//               <h2 className="text-lg font-semibold mb-4">Order Summary</h2>
//               <div className="space-y-2 mb-4">
//                 <div className="flex justify-between text-sm">
//                   <span>Subtotal</span>
//                   <span>{subtotal?.toFixed(2)} Rs</span>
//                 </div>
//                 <div className="flex justify-between text-sm">
//                   <span>Shipping Charges</span>
//                   <span>{shippingCharges?.toFixed(2)} Rs</span>
//                 </div>
//                 <div className="flex justify-between text-sm">
//                   <span>Taxes (5%)</span>
//                   <span>{taxes?.toFixed(2)} Rs</span>
//                 </div>
//                 {appliedDiscount > 0 && (
//                   <div className="flex justify-between text-sm text-green-600">
//                     <span>Discount</span>
//                     <span>-{appliedDiscount?.toFixed(2)} Rs</span>
//                   </div>
//                 )}
//                 <div className="flex justify-between font-semibold text-lg pt-2 border-t">
//                   <span>Total</span>
//                   <span>{total?.toFixed(2)} Rs</span>
//                 </div>
//               </div>
              
//               <button
//                 className={`w-full py-2 px-4 rounded-md transition duration-200 mb-4 ${
//                   cart.length === 0
//                     ? "bg-gray-400 text-gray-200 cursor-not-allowed"
//                     : "bg-blue-500 text-white hover:bg-blue-600"
//                 }`}
//                 onClick={handleContinue}
//                 disabled={cart?.length === 0}
//               >
//                 Continue
//               </button>
              
//               <div className="flex items-center mb-2">
//                 <input
//                   type="checkbox"
//                   id="useDiscount"
//                   className="mr-2"
//                   checked={showCouponInput}
//                   onChange={() => setShowCouponInput(!showCouponInput)}
//                   disabled={cart.length === 0}
//                 />
//                 <label 
//                   htmlFor="useDiscount" 
//                   className={`text-sm ${cart.length === 0 ? "text-gray-400" : "text-gray-600"}`}
//                 >
//                   Use discount code
//                 </label>
//               </div>
              
//               {showCouponInput && (
//                 <div className="flex space-x-2">
//                   <input
//                     type="text"
//                     placeholder="Enter code"
//                     value={couponCode}
//                     onChange={(e) => setCouponCode(e.target.value)}
//                     className="flex-grow px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
//                   />
//                   <button
//                     onClick={applyDiscount}
//                     className="bg-blue-500 text-white px-4 py-2 rounded-md hover:bg-blue-600 transition duration-200"
//                   >
//                     Apply
//                   </button>
//                 </div>
//               )}
//             </div>
//           </div>
//         </div>

//         {showShippingAddress && (
//           <ShippingAddress
//             onClose={() => setShowShippingAddress(false)}
//             onSubmit={handleShippingSubmit}
//             orderSummary={{
//               subtotal,
//               shippingCharges,
//               taxes,
//               appliedDiscount,
//               total,
//             }}
//           />
//         )}
//       </div>
//     </>
//   );
// };

// export default CartPage;

// import React, { useState,useEffect } from "react";
// import { useDispatch, useSelector } from "react-redux";
// import { removeFromCart, updateQuantity, setCartItems } from "../redux/slices/cartSlice";
// import ShippingAddress from "../Page/SubHeaderPage/Grocery/ShippingAddress";
// import { RiDeleteBin6Line } from "react-icons/ri";
// import Header from "../Page/Header/Header";
// import axios from "axios";
// import Cookies from "js-cookie";

// const CartPage = () => {
//   const cart = useSelector(state => state.cart.items);
//   const dispatch = useDispatch();
  
//   const [couponCode, setCouponCode] = useState("");
//   const [showCouponInput, setShowCouponInput] = useState(false);
//   const [appliedDiscount, setAppliedDiscount] = useState(0);
//   const [showShippingAddress, setShowShippingAddress] = useState(false);

//   // Function to parse price correctly
//   const parsePrice = (price) => {
//     if (typeof price === "number") return price;
    
//     if (typeof price === "string") {
//       const cleanedPrice = price.replace("₹", "").trim();
//       const parsedPrice = parseFloat(cleanedPrice) || 0;
//       return isNaN(parsedPrice) ? 0 : Math.max(0, parsedPrice);
//     }
    
//     return 0;
//   };

//   const calculateSubtotal = () => {
//     return cart.reduce((total, item) => {
//       const price = parsePrice(item?.price);
//       return total + price * item.quantity;
//     }, 0);
//   };

//   const applyDiscount = () => {
//     if (couponCode.toUpperCase() === "DISCOUNT10") {
//       const discount = calculateSubtotal() * 0.1;
//       setAppliedDiscount(discount);
//       alert("Discount applied successfully!");
//     } else {
//       alert("Invalid discount code");
//     }
//   };

//   useEffect(() => {
//   const fetchCart = async () => {
//     const token = Cookies.get("ecom_user_token");
//     try {
//       const response = await axios.get("https://seller-backend-29o9.onrender.com/api/buyer/get-cart", {
//         headers: {
//           Authorization: `Bearer ${token}`,
//         },
//       });

//       const items = response.data.cart.items.map((item) => ({
//         id: item.productId._id,
//         name: item.productId.name,
//         price: item.price,
//         quantity: item.quantity,
//         image: item.productId.image || "/default-product.jpg", // fallback image
//       }));

//       dispatch(setCartItems(items));
//     } catch (error) {
//       console.error("Failed to fetch cart:", error);
//     }
//   };

//   fetchCart();
// }, [dispatch]);


//   // const handleRemove = (id) => {
//   //   dispatch(removeFromCart(id));
//   // };

//   // const handleQuantityChange = (id, quantity) => {
//   //   // Ensure quantity doesn't go below 1
//   //   const newQuantity = Math.max(1, quantity);
//   //   dispatch(updateQuantity({ id, quantity: newQuantity }));
//   // };

// //   const handleQuantityChange = async (id, quantity,act) => {
// //   const inc = 1
// //   const dec = -1
// //   let a=0;
// //   const newQuantity = Math.max(1, quantity);
// //   const token = Cookies.get("ecom_user_token");
// //   if(act=='decrease'){a=dec}
// //   else{a=inc}
// //   try {
// //     await axios.post(
// //       "https://seller-backend-29o9.onrender.com/api/buyer/add-to-cart",
// //       {
// //         productId: id,
// //         quantity: quantity,
// //       },
// //       {
// //         headers: {
// //           Authorization: `Bearer ${token}`,
// //         },
// //       }
// //     );

// //     dispatch(updateQuantity({ id, quantity: newQuantity }));
// //   } catch (error) {
// //     console.error("Error updating quantity:", error);
// //     alert("Failed to update cart item.");
// //   }
// // };

// // const handleQuantityChange = async (id, quantity, act) => {
// //   const token = Cookies.get("ecom_user_token");


// //   try {
// //     await axios.post(
// //       "https://seller-backend-29o9.onrender.com/api/buyer/add-to-cart",
// //       {
// //         productId: id,
// //         quantity: quantity, // Use the calculated payload quantity
// //       },
// //       {
// //         headers: {
// //           Authorization: `Bearer ${token}`,
// //         },
// //       }
// //     );

// //     // Update local state with the new quantity (not the payload quantity)
// //     const newQuantity = Math.max(1, quantity);
// //     dispatch(updateQuantity({ id, quantity: newQuantity }));
// //   } catch (error) {
// //     console.error("Error updating quantity:", error);
// //     alert("Failed to update cart item.");
// //   }
// // };

// const handleQuantityChange = async (id, quantity, act) => {
//   const token = Cookies.get("ecom_user_token");
  
//   // If quantity is 0 or less, remove the item
//   if (quantity <= 0) {
//     try {
//       await axios.patch(
//         "https://seller-backend-29o9.onrender.com/api/buyer/remove-from-cart",
//         {
//           productId: id,
//         },
//         {
//           headers: {
//             Authorization: `Bearer ${token}`,
//           },
//         }
//       );
//       dispatch(removeFromCart(id));
//       return; // Exit the function after removal
//     } catch (error) {
//       console.error("Error removing item:", error);
//       alert("Failed to remove item from cart.");
//       return;
//     }
//   }

//   try {
//     await axios.post(
//       "https://seller-backend-29o9.onrender.com/api/buyer/add-to-cart",
//       {
//         productId: id,
//         quantity: quantity,
//       },
//       {
//         headers: {
//           Authorization: `Bearer ${token}`,
//         },
//       }
//     );

//     // Update local state with the new quantity
//     dispatch(updateQuantity({ id, quantity }));
//   } catch (error) {
//     console.error("Error updating quantity:", error);
//     alert("Failed to update cart item.");
//   }
// };
// const handleRemove = async (id) => {
//   const token = Cookies.get("ecom_user_token");

//   try {
//     await axios.patch(
//       "https://seller-backend-29o9.onrender.com/api/buyer/remove-from-cart",
//       {
//         productId: id,
//       },
//       {
//         headers: {
//           Authorization: `Bearer ${token}`,
//         },
//       }
//     );

//     dispatch(removeFromCart(id));
//   } catch (error) {
//     console.error("Error removing item:", error);
//     alert("Failed to remove item from cart.");
//   }
// };



//   const subtotal = calculateSubtotal();
//   const shippingCharges = 2.5;
//   const taxes = subtotal * 0.05;
//   const total = subtotal + shippingCharges + taxes - appliedDiscount;

//   const handleContinue = () => {
//     setShowShippingAddress(true);
//   };

//   const handleShippingSubmit = (address) => {
//     console.log("Shipping Address:", address);
//     setShowShippingAddress(false);
//   };

//   const cartItemCount = cart.reduce((total, item) => total + item.quantity, 0);

//   return (
//     <>
//     <Header/>
//     <div className="w-[96vw] mx-auto p-6 bg-gray-100 mt-8">
//       <h1 className="text-2xl font-bold mb-6 flex items-center">
//         <span className="text-xl text-blue-500 mr-2">🛒</span> Cart
//         {cartItemCount > 0 && (
//           <span className="ml-2 inline-flex items-center justify-center w-6 h-6 text-xs font-semibold text-white bg-red-600 rounded-full">
//             {cartItemCount}
//           </span>
//         )}
//       </h1>
      
//       <div className="flex flex-col lg:flex-row gap-8">
//         {/* Cart Items Table */}
//         <div className="lg:w-2/3">
//           <div className="bg-white rounded-lg shadow overflow-hidden">
//             {cart.length === 0 ? (
//               <div className="text-center py-12">
//                 <p className="text-gray-500 text-lg">Your cart is empty</p>
//               </div>
//             ) : (
//               <table className="w-full">
//                 <thead className="bg-gray-50">
//                   <tr>
//                     <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
//                       Product
//                     </th>
//                     <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
//                       Quantity
//                     </th>
//                     <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
//                       Subtotal
//                     </th>
//                     <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
//                       Actions
//                     </th>
//                   </tr>
//                 </thead>
//                 <tbody className="divide-y divide-gray-200">
//                   {cart.map((item) => {
//                     const price = parsePrice(item?.price);
//                     return (
//                       <tr key={item.id}>
//                         <td className="px-6 py-4 whitespace-nowrap">
//                           <div className="flex items-center">
//                             <div className="flex-shrink-0 h-20 w-20">
//                               <img
//                                 className="h-20 w-20 object-cover rounded"
//                                 src={item.image}
//                                 alt={item.title}
//                               />
//                             </div>
//                             <div className="ml-4">
//                               <div className="text-sm font-medium text-gray-900">
//                                 {item?.name}
//                               </div>
//                               <div className="text-sm text-gray-500">
//                                 {item?.price?.toFixed(2)} Rs
//                               </div>
//                             </div>
//                           </div>
//                         </td>
//                         <td className="px-6 py-4 whitespace-nowrap">
//                           <div className="flex items-center justify-center space-x-2">
//                            <button
//   className={`w-8 h-8 flex items-center justify-center rounded-md transition-colors ${
//     item.quantity <= 1
//       ? "bg-gray-100 text-gray-400"
//       : "bg-gray-200 hover:bg-gray-300"
//   }`}
//   onClick={() => 
//     handleQuantityChange(item.id, item?.quantity - 1, 'decrease')
//   }
// >
//   <span className="text-lg">−</span>
// </button>
//                             <span className="w-8 text-center">
//                               {item?.quantity}
//                             </span>
//                             <button
//                               className="w-8 h-8 flex items-center justify-center bg-gray-200 rounded-md hover:bg-gray-300 transition-colors"
//                               onClick={() => 
//                                 handleQuantityChange(item.id, item?.quantity + 1,`increase`)
//                               }
//                             >
//                               <span className="text-lg">+</span>
//                             </button>
//                           </div>
//                         </td>
//                         <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
//                           {(item?.price * item?.quantity)?.toFixed(2)} Rs
//                         </td>
//                         <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
//                           <button
//                             onClick={() => handleRemove(item.id)}
//                             className="text-red-600 hover:text-red-900 transition-colors"
//                           >
//                             <span className="text-lg"><RiDeleteBin6Line/></span>
//                           </button>
//                         </td>
//                       </tr>
//                     );
//                   })}
//                 </tbody>
//               </table>
//             )}
//           </div>
//         </div>

//         {/* Order Summary */}
//         <div className="lg:w-1/3">
//           <div className="bg-white rounded-lg shadow p-6">
//             <h2 className="text-lg font-semibold mb-4">Order Summary</h2>
//             <div className="space-y-2 mb-4">
//               <div className="flex justify-between text-sm">
//                 <span>Subtotal</span>
//                 <span>{subtotal?.toFixed(2)} Rs</span>
//               </div>
//               <div className="flex justify-between text-sm">
//                 <span>Shipping Charges</span>
//                 <span>{shippingCharges?.toFixed(2)} Rs</span>
//               </div>
//               <div className="flex justify-between text-sm">
//                 <span>Taxes (5%)</span>
//                 <span>{taxes?.toFixed(2)} Rs</span>
//               </div>
//               {appliedDiscount > 0 && (
//                 <div className="flex justify-between text-sm text-green-600">
//                   <span>Discount</span>
//                   <span>-{appliedDiscount?.toFixed(2)} Rs</span>
//                 </div>
//               )}
//               <div className="flex justify-between font-semibold text-lg pt-2 border-t">
//                 <span>Total</span>
//                 <span>{total?.toFixed(2)} Rs</span>
//               </div>
//             </div>
            
//             <button
//               className={`w-full py-2 px-4 rounded-md transition duration-200 mb-4 ${
//                 cart.length === 0
//                   ? "bg-gray-400 text-gray-200 cursor-not-allowed"
//                   : "bg-blue-500 text-white hover:bg-blue-600"
//               }`}
//               onClick={handleContinue}
//               disabled={cart?.length === 0}
//             >
//               Continue
//             </button>
            
//             <div className="flex items-center mb-2">
//               <input
//                 type="checkbox"
//                 id="useDiscount"
//                 className="mr-2"
//                 checked={showCouponInput}
//                 onChange={() => setShowCouponInput(!showCouponInput)}
//                 disabled={cart.length === 0}
//               />
//               <label 
//                 htmlFor="useDiscount" 
//                 className={`text-sm ${cart.length === 0 ? "text-gray-400" : "text-gray-600"}`}
//               >
//                 Use discount code
//               </label>
//             </div>
            
//             {showCouponInput && (
//               <div className="flex space-x-2">
//                 <input
//                   type="text"
//                   placeholder="Enter code"
//                   value={couponCode}
//                   onChange={(e) => setCouponCode(e.target.value)}
//                   className="flex-grow px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
//                 />
//                 <button
//                   onClick={applyDiscount}
//                   className="bg-blue-500 text-white px-4 py-2 rounded-md hover:bg-blue-600 transition duration-200"
//                 >
//                   Apply
//                 </button>
//               </div>
//             )}
//           </div>
//         </div>
//       </div>

//       {showShippingAddress && (
//         <ShippingAddress
//           onClose={() => setShowShippingAddress(false)}
//           onSubmit={handleShippingSubmit}
//           orderSummary={{
//             subtotal,
//             shippingCharges,
//             taxes,
//             appliedDiscount,
//             total,
//           }}
//         />
//       )}
//     </div>
//     </>
//   );
// };

// export default CartPage;

// import React, { useState,useEffect } from "react";
// import { useDispatch, useSelector } from "react-redux";
// import { removeFromCart, updateQuantity, setCartItems } from "../redux/slices/cartSlice";
// import ShippingAddress from "../Page/SubHeaderPage/Grocery/ShippingAddress";
// import { RiDeleteBin6Line } from "react-icons/ri";
// import Header from "../Page/Header/Header";
// import axios from "axios";
// import Cookies from "js-cookie";

// const CartPage = () => {
//   const cart = useSelector(state => state.cart.items);
//   const dispatch = useDispatch();
  
//   const [couponCode, setCouponCode] = useState("");
//   const [showCouponInput, setShowCouponInput] = useState(false);
//   const [appliedDiscount, setAppliedDiscount] = useState(0);
//   const [showShippingAddress, setShowShippingAddress] = useState(false);

//   // Function to parse price correctly
//   const parsePrice = (price) => {
//     if (typeof price === "number") return price;
    
//     if (typeof price === "string") {
//       const cleanedPrice = price.replace("₹", "").trim();
//       const parsedPrice = parseFloat(cleanedPrice) || 0;
//       return isNaN(parsedPrice) ? 0 : Math.max(0, parsedPrice);
//     }
    
//     return 0;
//   };

//   const calculateSubtotal = () => {
//     return cart.reduce((total, item) => {
//       const price = parsePrice(item?.price);
//       return total + price * item.quantity;
//     }, 0);
//   };

//   const applyDiscount = () => {
//     if (couponCode.toUpperCase() === "DISCOUNT10") {
//       const discount = calculateSubtotal() * 0.1;
//       setAppliedDiscount(discount);
//       alert("Discount applied successfully!");
//     } else {
//       alert("Invalid discount code");
//     }
//   };

//   useEffect(() => {
//   const fetchCart = async () => {
//     const token = Cookies.get("ecom_user_token");
//     try {
//       const response = await axios.get("https://seller-backend-29o9.onrender.com/api/buyer/get-cart", {
//         headers: {
//           Authorization: `Bearer ${token}`,
//         },
//       });

//       const items = response.data.cart.items.map((item) => ({
//         id: item.productId._id,
//         name: item.productId.name,
//         price: item.price,
//         quantity: item.quantity,
//         image: item.productId.image || "/default-product.jpg", // fallback image
//       }));

//       dispatch(setCartItems(items));
//     } catch (error) {
//       console.error("Failed to fetch cart:", error);
//     }
//   };

//   fetchCart();
// }, [dispatch]);


//   // const handleRemove = (id) => {
//   //   dispatch(removeFromCart(id));
//   // };

//   // const handleQuantityChange = (id, quantity) => {
//   //   // Ensure quantity doesn't go below 1
//   //   const newQuantity = Math.max(1, quantity);
//   //   dispatch(updateQuantity({ id, quantity: newQuantity }));
//   // };

// //   const handleQuantityChange = async (id, quantity,act) => {
// //   const inc = 1
// //   const dec = -1
// //   let a=0;
// //   const newQuantity = Math.max(1, quantity);
// //   const token = Cookies.get("ecom_user_token");
// //   if(act=='decrease'){a=dec}
// //   else{a=inc}
// //   try {
// //     await axios.post(
// //       "https://seller-backend-29o9.onrender.com/api/buyer/add-to-cart",
// //       {
// //         productId: id,
// //         quantity: quantity,
// //       },
// //       {
// //         headers: {
// //           Authorization: `Bearer ${token}`,
// //         },
// //       }
// //     );

// //     dispatch(updateQuantity({ id, quantity: newQuantity }));
// //   } catch (error) {
// //     console.error("Error updating quantity:", error);
// //     alert("Failed to update cart item.");
// //   }
// // };

// // const handleQuantityChange = async (id, quantity, act) => {
// //   const token = Cookies.get("ecom_user_token");


// //   try {
// //     await axios.post(
// //       "https://seller-backend-29o9.onrender.com/api/buyer/add-to-cart",
// //       {
// //         productId: id,
// //         quantity: quantity, // Use the calculated payload quantity
// //       },
// //       {
// //         headers: {
// //           Authorization: `Bearer ${token}`,
// //         },
// //       }
// //     );

// //     // Update local state with the new quantity (not the payload quantity)
// //     const newQuantity = Math.max(1, quantity);
// //     dispatch(updateQuantity({ id, quantity: newQuantity }));
// //   } catch (error) {
// //     console.error("Error updating quantity:", error);
// //     alert("Failed to update cart item.");
// //   }
// // };

// const handleQuantityChange = async (id, quantity, act) => {
//   const token = Cookies.get("ecom_user_token");
  
//   // If quantity is 0 or less, remove the item
//   if (quantity <= 0) {
//     try {
//       await axios.patch(
//         "https://seller-backend-29o9.onrender.com/api/buyer/remove-from-cart",
//         {
//           productId: id,
//         },
//         {
//           headers: {
//             Authorization: `Bearer ${token}`,
//           },
//         }
//       );
//       dispatch(removeFromCart(id));
//       return; // Exit the function after removal
//     } catch (error) {
//       console.error("Error removing item:", error);
//       alert("Failed to remove item from cart.");
//       return;
//     }
//   }

//   try {
//     await axios.post(
//       "https://seller-backend-29o9.onrender.com/api/buyer/add-to-cart",
//       {
//         productId: id,
//         quantity: quantity,
//       },
//       {
//         headers: {
//           Authorization: `Bearer ${token}`,
//         },
//       }
//     );

//     // Update local state with the new quantity
//     dispatch(updateQuantity({ id, quantity }));
//   } catch (error) {
//     console.error("Error updating quantity:", error);
//     alert("Failed to update cart item.");
//   }
// };
// const handleRemove = async (id) => {
//   const token = Cookies.get("ecom_user_token");

//   try {
//     await axios.patch(
//       "https://seller-backend-29o9.onrender.com/api/buyer/remove-from-cart",
//       {
//         productId: id,
//       },
//       {
//         headers: {
//           Authorization: `Bearer ${token}`,
//         },
//       }
//     );

//     dispatch(removeFromCart(id));
//   } catch (error) {
//     console.error("Error removing item:", error);
//     alert("Failed to remove item from cart.");
//   }
// };



//   const subtotal = calculateSubtotal();
//   const shippingCharges = 2.5;
//   const taxes = subtotal * 0.05;
//   const total = subtotal + shippingCharges + taxes - appliedDiscount;

//   const handleContinue = () => {
//     setShowShippingAddress(true);
//   };

//   const handleShippingSubmit = (address) => {
//     console.log("Shipping Address:", address);
//     setShowShippingAddress(false);
//   };

//   const cartItemCount = cart.reduce((total, item) => total + item.quantity, 0);

//   return (
//     <>
//     <Header/>
//     <div className="w-[96vw] mx-auto p-6 bg-gray-100 mt-8">
//       <h1 className="text-2xl font-bold mb-6 flex items-center">
//         <span className="text-xl text-blue-500 mr-2">🛒</span> Cart
//         {cartItemCount > 0 && (
//           <span className="ml-2 inline-flex items-center justify-center w-6 h-6 text-xs font-semibold text-white bg-red-600 rounded-full">
//             {cartItemCount}
//           </span>
//         )}
//       </h1>
      
//       <div className="flex flex-col lg:flex-row gap-8">
//         {/* Cart Items Table */}
//         <div className="lg:w-2/3">
//           <div className="bg-white rounded-lg shadow overflow-hidden">
//             {cart.length === 0 ? (
//               <div className="text-center py-12">
//                 <p className="text-gray-500 text-lg">Your cart is empty</p>
//               </div>
//             ) : (
//               <table className="w-full">
//                 <thead className="bg-gray-50">
//                   <tr>
//                     <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
//                       Product
//                     </th>
//                     <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
//                       Quantity
//                     </th>
//                     <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
//                       Subtotal
//                     </th>
//                     <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
//                       Actions
//                     </th>
//                   </tr>
//                 </thead>
//                 <tbody className="divide-y divide-gray-200">
//                   {cart.map((item) => {
//                     const price = parsePrice(item?.price);
//                     return (
//                       <tr key={item.id}>
//                         <td className="px-6 py-4 whitespace-nowrap">
//                           <div className="flex items-center">
//                             <div className="flex-shrink-0 h-20 w-20">
//                               <img
//                                 className="h-20 w-20 object-cover rounded"
//                                 src={item.image}
//                                 alt={item.title}
//                               />
//                             </div>
//                             <div className="ml-4">
//                               <div className="text-sm font-medium text-gray-900">
//                                 {item?.name}
//                               </div>
//                               <div className="text-sm text-gray-500">
//                                 {item?.price?.toFixed(2)} Rs
//                               </div>
//                             </div>
//                           </div>
//                         </td>
//                         <td className="px-6 py-4 whitespace-nowrap">
//                           <div className="flex items-center justify-center space-x-2">
//                            <button
//   className={`w-8 h-8 flex items-center justify-center rounded-md transition-colors ${
//     item.quantity <= 1
//       ? "bg-gray-100 text-gray-400"
//       : "bg-gray-200 hover:bg-gray-300"
//   }`}
//   onClick={() => 
//     handleQuantityChange(item.id, item?.quantity - 1, 'decrease')
//   }
// >
//   <span className="text-lg">−</span>
// </button>
//                             <span className="w-8 text-center">
//                               {item?.quantity}
//                             </span>
//                             <button
//                               className="w-8 h-8 flex items-center justify-center bg-gray-200 rounded-md hover:bg-gray-300 transition-colors"
//                               onClick={() => 
//                                 handleQuantityChange(item.id, item?.quantity + 1,`increase`)
//                               }
//                             >
//                               <span className="text-lg">+</span>
//                             </button>
//                           </div>
//                         </td>
//                         <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
//                           {(item?.price * item?.quantity)?.toFixed(2)} Rs
//                         </td>
//                         <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
//                           <button
//                             onClick={() => handleRemove(item.id)}
//                             className="text-red-600 hover:text-red-900 transition-colors"
//                           >
//                             <span className="text-lg"><RiDeleteBin6Line/></span>
//                           </button>
//                         </td>
//                       </tr>
//                     );
//                   })}
//                 </tbody>
//               </table>
//             )}
//           </div>
//         </div>

//         {/* Order Summary */}
//         <div className="lg:w-1/3">
//           <div className="bg-white rounded-lg shadow p-6">
//             <h2 className="text-lg font-semibold mb-4">Order Summary</h2>
//             <div className="space-y-2 mb-4">
//               <div className="flex justify-between text-sm">
//                 <span>Subtotal</span>
//                 <span>{subtotal?.toFixed(2)} Rs</span>
//               </div>
//               <div className="flex justify-between text-sm">
//                 <span>Shipping Charges</span>
//                 <span>{shippingCharges?.toFixed(2)} Rs</span>
//               </div>
//               <div className="flex justify-between text-sm">
//                 <span>Taxes (5%)</span>
//                 <span>{taxes?.toFixed(2)} Rs</span>
//               </div>
//               {appliedDiscount > 0 && (
//                 <div className="flex justify-between text-sm text-green-600">
//                   <span>Discount</span>
//                   <span>-{appliedDiscount?.toFixed(2)} Rs</span>
//                 </div>
//               )}
//               <div className="flex justify-between font-semibold text-lg pt-2 border-t">
//                 <span>Total</span>
//                 <span>{total?.toFixed(2)} Rs</span>
//               </div>
//             </div>
            
//             <button
//               className={`w-full py-2 px-4 rounded-md transition duration-200 mb-4 ${
//                 cart.length === 0
//                   ? "bg-gray-400 text-gray-200 cursor-not-allowed"
//                   : "bg-blue-500 text-white hover:bg-blue-600"
//               }`}
//               onClick={handleContinue}
//               disabled={cart?.length === 0}
//             >
//               Continue
//             </button>
            
//             <div className="flex items-center mb-2">
//               <input
//                 type="checkbox"
//                 id="useDiscount"
//                 className="mr-2"
//                 checked={showCouponInput}
//                 onChange={() => setShowCouponInput(!showCouponInput)}
//                 disabled={cart.length === 0}
//               />
//               <label 
//                 htmlFor="useDiscount" 
//                 className={`text-sm ${cart.length === 0 ? "text-gray-400" : "text-gray-600"}`}
//               >
//                 Use discount code
//               </label>
//             </div>
            
//             {showCouponInput && (
//               <div className="flex space-x-2">
//                 <input
//                   type="text"
//                   placeholder="Enter code"
//                   value={couponCode}
//                   onChange={(e) => setCouponCode(e.target.value)}
//                   className="flex-grow px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
//                 />
//                 <button
//                   onClick={applyDiscount}
//                   className="bg-blue-500 text-white px-4 py-2 rounded-md hover:bg-blue-600 transition duration-200"
//                 >
//                   Apply
//                 </button>
//               </div>
//             )}
//           </div>
//         </div>
//       </div>

//       {showShippingAddress && (
//         <ShippingAddress
//           onClose={() => setShowShippingAddress(false)}
//           onSubmit={handleShippingSubmit}
//           orderSummary={{
//             subtotal,
//             shippingCharges,
//             taxes,
//             appliedDiscount,
//             total,
//           }}
//         />
//       )}
//     </div>
//     </>
//   );
// };

// export default CartPage;

// import React, { useState } from "react";
// import { useDispatch, useSelector } from "react-redux";
// import ShippingAddress from "../Page/SubHeaderPage/Grocery/ShippingAddress";

// const CartPage = () => {
//   const cart = useSelector(state => state.cart.items);
//   const dispatch = useDispatch();
  
//   const [couponCode, setCouponCode] = useState("");
//   const [showCouponInput, setShowCouponInput] = useState(false);
//   const [appliedDiscount, setAppliedDiscount] = useState(0);
//   const [showShippingAddress, setShowShippingAddress] = useState(false);

//   // Function to parse price correctly
//   const parsePrice = (price) => {
//     if (typeof price === "number") return price;
    
//     if (typeof price === "string") {
//       const cleanedPrice = price.replace("₹", "").trim();
//       const parsedPrice = parseFloat(cleanedPrice) || 0;
//       return isNaN(parsedPrice) ? 0 : Math.max(0, parsedPrice);
//     }
    
//     return 0;
//   };

//   const calculateSubtotal = () => {
//     return cart.reduce((total, item) => {
//       const price = parsePrice(item.discountPrice);
//       return total + price * item.quantity;
//     }, 0);
//   };

//   const applyDiscount = () => {
//     if (couponCode.toUpperCase() === "DISCOUNT10") {
//       const discount = calculateSubtotal() * 0.1;
//       setAppliedDiscount(discount);
//       alert("Discount applied successfully!");
//     } else {
//       alert("Invalid discount code");
//     }
//   };

//   const handleRemove = (id) => {
//     dispatch(removeFromCart(id));
//   };

//   const handleQuantityChange = (id, quantity) => {
//     dispatch(updateQuantity({ id, quantity }));
//   };

//   const subtotal = calculateSubtotal();
//   const shippingCharges = 2.5;
//   const taxes = subtotal * 0.05;
//   const total = subtotal + shippingCharges + taxes - appliedDiscount;

//   const handleContinue = () => {
//     setShowShippingAddress(true);
//   };

//   const handleShippingSubmit = (address) => {
//     console.log("Shipping Address:", address);
//     setShowShippingAddress(false);
//   };

//   const cartItemCount = cart.reduce((total, item) => total + item.quantity, 0);

//   return (
//     <div className="max-w-7xl mx-auto p-6 bg-gray-100">
//       <h1 className="text-2xl font-bold mb-6 flex items-center">
//         <span className="text-xl text-blue-500 mr-2">🛒</span> Cart
//         {cartItemCount > 0 && (
//           <span className="ml-2 inline-flex items-center justify-center w-6 h-6 text-xs font-semibold text-white bg-red-600 rounded-full">
//             {cartItemCount}
//           </span>
//         )}
//       </h1>
      
//       <div className="flex flex-col lg:flex-row gap-8">
//         {/* Cart Items Table */}
//         <div className="lg:w-2/3">
//           <div className="bg-white rounded-lg shadow overflow-hidden">
//             {cart.length === 0 ? (
//               <div className="text-center py-12">
//                 <p className="text-gray-500 text-lg">Your cart is empty</p>
//               </div>
//             ) : (
//               <table className="w-full">
//                 <thead className="bg-gray-50">
//                   <tr>
//                     <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
//                       Product
//                     </th>
//                     <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
//                       Quantity
//                     </th>
//                     <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
//                       Subtotal
//                     </th>
//                     <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
//                       Actions
//                     </th>
//                   </tr>
//                 </thead>
//                 <tbody className="divide-y divide-gray-200">
//                   {cart.map((item) => {
//                     const price = parsePrice(item.discountPrice);
//                     return (
//                       <tr key={item.id}>
//                         <td className="px-6 py-4 whitespace-nowrap">
//                           <div className="flex items-center">
//                             <div className="flex-shrink-0 h-20 w-20">
//                               <img
//                                 className="h-20 w-20 object-cover rounded"
//                                 src={item.image}
//                                 alt={item.title}
//                               />
//                             </div>
//                             <div className="ml-4">
//                               <div className="text-sm font-medium text-gray-900">
//                                 {item.title}
//                               </div>
//                               <div className="text-sm text-gray-500">
//                                 {price.toFixed(2)} Rs
//                               </div>
//                             </div>
//                           </div>
//                         </td>
//                         <td className="px-6 py-4 whitespace-nowrap">
//                           <div className="flex items-center justify-center space-x-2">
//                             <button
//                               className="w-8 h-8 flex items-center justify-center bg-gray-200 rounded-md hover:bg-gray-300 transition-colors"
//                               onClick={() => 
//                                 handleQuantityChange(item.id, item.quantity - 1)
//                               }
//                               disabled={item.quantity <= 1}
//                             >
//                               <span className="text-lg">−</span>
//                             </button>
//                             <span className="w-8 text-center">
//                               {item.quantity}
//                             </span>
//                             <button
//                               className="w-8 h-8 flex items-center justify-center bg-gray-200 rounded-md hover:bg-gray-300 transition-colors"
//                               onClick={() => 
//                                 handleQuantityChange(item.id, item.quantity + 1)
//                               }
//                             >
//                               <span className="text-lg">+</span>
//                             </button>
//                           </div>
//                         </td>
//                         <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
//                           {(price * item.quantity).toFixed(2)} Rs
//                         </td>
//                         <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
//                           <button
//                             onClick={() => handleRemove(item.id)}
//                             className="text-red-600 hover:text-red-900 transition-colors"
//                           >
//                             <span className="text-lg">🗑️</span>
//                           </button>
//                         </td>
//                       </tr>
//                     );
//                   })}
//                 </tbody>
//               </table>
//             )}
//           </div>
//         </div>

//         {/* Order Summary */}
//         <div className="lg:w-1/3">
//           <div className="bg-white rounded-lg shadow p-6">
//             <h2 className="text-lg font-semibold mb-4">Order Summary</h2>
//             <div className="space-y-2 mb-4">
//               <div className="flex justify-between text-sm">
//                 <span>Subtotal</span>
//                 <span>{subtotal.toFixed(2)} Rs</span>
//               </div>
//               <div className="flex justify-between text-sm">
//                 <span>Shipping Charges</span>
//                 <span>{shippingCharges.toFixed(2)} Rs</span>
//               </div>
//               <div className="flex justify-between text-sm">
//                 <span>Taxes (5%)</span>
//                 <span>{taxes.toFixed(2)} Rs</span>
//               </div>
//               {appliedDiscount > 0 && (
//                 <div className="flex justify-between text-sm text-green-600">
//                   <span>Discount</span>
//                   <span>-{appliedDiscount.toFixed(2)} Rs</span>
//                 </div>
//               )}
//               <div className="flex justify-between font-semibold text-lg pt-2 border-t">
//                 <span>Total</span>
//                 <span>{total.toFixed(2)} Rs</span>
//               </div>
//             </div>
            
//             <button
//               className={`w-full py-2 px-4 rounded-md transition duration-200 mb-4 ${
//                 cart.length === 0
//                   ? "bg-gray-400 cursor-not-allowed"
//                   : "bg-blue-500 text-white hover:bg-blue-600"
//               }`}
//               onClick={handleContinue}
//               disabled={cart.length === 0}
//             >
//               Continue
//             </button>
            
//             <div className="flex items-center mb-2">
//               <input
//                 type="checkbox"
//                 id="useDiscount"
//                 className="mr-2"
//                 checked={showCouponInput}
//                 onChange={() => setShowCouponInput(!showCouponInput)}
//               />
//               <label htmlFor="useDiscount" className="text-sm text-gray-600">
//                 Use discount code
//               </label>
//             </div>
            
//             {showCouponInput && (
//               <div className="flex space-x-2">
//                 <input
//                   type="text"
//                   placeholder="Enter code"
//                   value={couponCode}
//                   onChange={(e) => setCouponCode(e.target.value)}
//                   className="flex-grow px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
//                 />
//                 <button
//                   onClick={applyDiscount}
//                   className="bg-blue-500 text-white px-4 py-2 rounded-md hover:bg-blue-600 transition duration-200"
//                 >
//                   Apply
//                 </button>
//               </div>
//             )}
//           </div>
//         </div>
//       </div>

//       {showShippingAddress && (
//         <ShippingAddress
//           onClose={() => setShowShippingAddress(false)}
//           onSubmit={handleShippingSubmit}
//           orderSummary={{
//             subtotal,
//             shippingCharges,
//             taxes,
//             appliedDiscount,
//             total,
//           }}
//         />
//       )}
//     </div>
//   );
// };

// export default CartPage;

// import React, { useState } from "react";
// import { useCart } from "../Page/SubHeaderPage/Grocery/CartContext";
// import ShippingAddress from "../Page/SubHeaderPage/Grocery/ShippingAddress";

// const CartPage = () => {
//   const { cart, removeFromCart, updateQuantity } = useCart();
//   const [couponCode, setCouponCode] = useState("");
//   const [showCouponInput, setShowCouponInput] = useState(false);
//   const [appliedDiscount, setAppliedDiscount] = useState(0);
//   const [showShippingAddress, setShowShippingAddress] = useState(false);

//   // Function to parse price correctly
//   const parsePrice = (price) => {
//     if (typeof price === "number") {
//       return price;
//     }
//     // Handle the case where the price is a string
//     if (typeof price === "string") {
//       const cleanedPrice = price.replace("₹", "").trim();
//       const parsedPrice = cleanedPrice ? parseFloat(cleanedPrice) : 0;
//       return isNaN(parsedPrice) || parsedPrice < 0 ? 0 : parsedPrice;
//     }
//     return 0;
//   };

//   const calculateSubtotal = () => {
//     return cart.reduce((total, item) => {
//       const price = parsePrice(item.discountPrice);
//       return total + price * item.quantity;
//     }, 0);
//   };

//   const applyDiscount = () => {
//     if (couponCode.toUpperCase() === "DISCOUNT10") {
//       const discount = calculateSubtotal() * 0.1;
//       setAppliedDiscount(discount);
//       alert("Discount applied successfully!");
//     } else {
//       alert("Invalid discount code");
//     }
//   };

//   const subtotal = calculateSubtotal();
//   const shippingCharges = 2.5;
//   const taxes = subtotal * 0.05;
//   const total = subtotal + shippingCharges + taxes - appliedDiscount;

//   const handleContinue = () => {
//     setShowShippingAddress(true);
//   };

//   const handleShippingSubmit = (address) => {
//     console.log("Shipping Address:", address);
//     setShowShippingAddress(false);
//   };

//   const cartItemCount = cart.reduce((total, item) => total + item.quantity, 0); // Total items in cart

//   return (
//     <div className="max-w-7xl mx-auto p-6 bg-gray-100">
//       <h1 className="text-2xl font-bold mb-6 flex items-center">
//         <span className="text-xl text-blue-500 mr-2">🛒</span> Cart
//         {/* Cart badge */}
//         {cartItemCount > 0 && (
//           <span className="ml-2 inline-flex items-center justify-center w-6 h-6 text-xs font-semibold text-white bg-red-600 rounded-full">
//             {cartItemCount}
//           </span>
//         )}
//       </h1>
//       <div className="flex flex-col lg:flex-row gap-8">
//         <div className="lg:w-2/3">
//           <div className="bg-white rounded-lg shadow overflow-hidden">
//             <table className="w-full">
//               <thead className="bg-gray-50">
//                 <tr>
//                   <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
//                     Product
//                   </th>
//                   <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
//                     Quantity
//                   </th>
//                   <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
//                     Subtotal
//                   </th>
//                   <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
//                     Actions
//                   </th>
//                 </tr>
//               </thead>
//               <tbody className="divide-y divide-gray-200">
//                 {cart.map((item) => {
//                   const price = parsePrice(item.discountPrice);
//                   return (
//                     <tr key={item.id}>
//                       <td className="px-6 py-4 whitespace-nowrap">
//                         <div className="flex items-center">
//                           <div className="flex-shrink-0 h-20 w-20">
//                             <img
//                               className="h-20 w-20 object-cover rounded"
//                               src={item.image}
//                               alt={item.title}
//                             />
//                           </div>
//                           <div className="ml-4">
//                             <div className="text-sm font-medium text-gray-900">
//                               {item.title}
//                             </div>
//                             <div className="text-sm text-gray-500">
//                               {price.toFixed(2)} Rs
//                             </div>
//                           </div>
//                         </div>
//                       </td>
//                       <td className="px-6 py-4 whitespace-nowrap">
//                         <div className="flex items-center justify-center space-x-2">
//                           <button
//                             className="w-8 h-8 flex items-center justify-center bg-gray-200 rounded-md hover:bg-gray-300 transition-colors"
//                             onClick={() =>
//                               updateQuantity(item.id, item.quantity - 1)
//                             }
//                           >
//                             <span className="text-lg">−</span>
//                           </button>
//                           <span className="w-8 text-center">
//                             {item.quantity}
//                           </span>
//                           <button
//                             className="w-8 h-8 flex items-center justify-center bg-gray-200 rounded-md hover:bg-gray-300 transition-colors"
//                             onClick={() =>
//                               updateQuantity(item.id, item.quantity + 1)
//                             }
//                           >
//                             <span className="text-lg">+</span>
//                           </button>
//                         </div>
//                       </td>
//                       <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
//                         {(price * item.quantity).toFixed(2)} Rs
//                       </td>
//                       <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
//                         <button
//                           onClick={() => removeFromCart(item.id)}
//                           className="text-red-600 hover:text-red-900 transition-colors"
//                         >
//                           <span className="text-lg">🗑️</span>
//                         </button>
//                       </td>
//                     </tr>
//                   );
//                 })}
//               </tbody>
//             </table>
//           </div>
//         </div>

//         <div className="lg:w-1/3">
//           <div className="bg-white rounded-lg shadow p-6">
//             <h2 className="text-lg font-semibold mb-4">Order Summary</h2>
//             <div className="space-y-2 mb-4">
//               <div className="flex justify-between text-sm">
//                 <span>Subtotal</span>
//                 <span>{subtotal.toFixed(2)} Rs</span>
//               </div>
//               <div className="flex justify-between text-sm">
//                 <span>Shipping Charges</span>
//                 <span>{shippingCharges.toFixed(2)} Rs</span>
//               </div>
//               <div className="flex justify-between text-sm">
//                 <span>Taxes (5%)</span>
//                 <span>{taxes.toFixed(2)} Rs</span>
//               </div>
//               {appliedDiscount > 0 && (
//                 <div className="flex justify-between text-sm text-green-600">
//                   <span>Discount</span>
//                   <span>-{appliedDiscount.toFixed(2)} Rs</span>
//                 </div>
//               )}
//               <div className="flex justify-between font-semibold text-lg pt-2 border-t">
//                 <span>Total</span>
//                 <span>{total.toFixed(2)} Rs</span>
//               </div>
//             </div>
//             <button
//               className="w-full bg-blue-500 text-white py-2 px-4 rounded-md hover:bg-blue-600 transition duration-200 mb-4"
//               onClick={handleContinue}
//             >
//               Continue
//             </button>
//             <div className="flex items-center mb-2">
//               <input
//                 type="checkbox"
//                 id="useDiscount"
//                 className="mr-2"
//                 checked={showCouponInput}
//                 onChange={() => setShowCouponInput(!showCouponInput)}
//               />
//               <label htmlFor="useDiscount" className="text-sm text-gray-600">
//                 Use discount code
//               </label>
//             </div>
//             {showCouponInput && (
//               <div className="flex space-x-2">
//                 <input
//                   type="text"
//                   placeholder="Enter code"
//                   value={couponCode}
//                   onChange={(e) => setCouponCode(e.target.value)}
//                   className="flex-grow px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
//                 />
//                 <button
//                   onClick={applyDiscount}
//                   className="bg-blue-500 text-white px-4 py-2 rounded-md hover:bg-blue-600 transition duration-200"
//                 >
//                   Apply Now
//                 </button>
//               </div>
//             )}
//           </div>
//         </div>
//       </div>

//       {showShippingAddress && (
//         <ShippingAddress
//           onClose={() => setShowShippingAddress(false)}
//           onSubmit={handleShippingSubmit}
//           orderSummary={{
//             subtotal,
//             shippingCharges,
//             taxes,
//             appliedDiscount,
//             total,
//           }}
//         />
//       )}
//     </div>
//   );
// };

// export default CartPage;
